import { forwardRef, useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useSeguirOFim } from '@/hooks/useSeguirOFim'
import {
  Stack, Text, Box, Tabs, Table, Badge, Skeleton, Alert,
  Modal, Textarea, TextInput, Button, Group, ActionIcon, Tooltip, Loader,
  Drawer, ScrollArea, Code, Checkbox, Divider, RingProgress, Center, SimpleGrid, Progress, Select,
  Collapse, UnstyledButton,
} from '@mantine/core'
import { WidgetCard, ResizableDrawer } from '@/components/ui'
import { useResizableDrawer } from '@/hooks/useResizableDrawer'
import { AreaChart } from '@mantine/charts'
import { IconAlertCircle, IconRefresh, IconPower, IconBrandDocker, IconPin, IconX, IconPlayerPlay, IconRotate, IconArrowUp, IconArrowDown, IconArrowsUpDown, IconSearch, IconRocket, IconDeviceDesktopAnalytics, IconNetwork, IconActivity, IconServer, IconEye, IconEyeOff, IconCopy, IconPencil, IconDeviceFloppy, IconAlertTriangle, IconFolder, IconChevronRight } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { JanelaFlutuante } from '@/components/ui/JanelaFlutuante'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { erroAoSalvarSecret } from './erroAoSalvarSecret'
import { abaInicial, secretDoLink } from './linkDiretoDoSecret'
import { useSearchParams } from 'react-router-dom'
import { useCurrentTenant } from '@/store/auth'
import { FileBrowserDrawer, type FileBrowserTarget, type PinnedFileBrowser } from '@/components/files/FileBrowserDrawer'
import { FloatingFileBrowser } from '@/components/files/FloatingFileBrowser'
import {
  COLOR,
  DeployLifecycleTimeline,
  DeployTimeline,
  HardRestartModal,
  type HardRestartTarget,
  HeroCardiograph,
  InstanceCard,
  MOTION,
  SURFACE,
  formatElapsed,
  parseDeployOutput,
  useTicker,
} from './components/HardRestart'
import { AnimatePresence, motion } from 'framer-motion'
import { VpnPanel, type VpnApiResponse } from './VpnTab'

// ── Types ──────────────────────────────────────────────────────────────────────

interface EC2Instance {
  instance_id: string
  name?: string
  instance_type: string
  state: string
  environment?: string
  region?: string
  public_ip?: string
  private_ip?: string
}

interface DockerContainer {
  id: string
  name: string
  image: string
  status: string
  state: string
  ports: string
  running_for: string
}

interface ElbListener {
  port: number
  protocol: string
}

interface ElbTarget {
  target_id: string
  target_name?: string
  port: number
  health_status: string
}

interface ElbTargetGroup {
  name: string
  arn: string
  protocol: string
  port: number
  targets: ElbTarget[]
  hc_protocol?: string
  hc_port?: string
  hc_path?: string
}

interface ElbItem {
  name: string
  arn?: string
  dns_name?: string
  scheme?: string
  state?: string
  type?: string
  lb_type?: string
  region?: string
  listeners?: ElbListener[]
  target_groups?: ElbTargetGroup[]
}

interface DomainItem {
  zone_id?: string
  name?: string
  domain_name?: string
  private_zone?: boolean
  record_set_count?: number
  status?: string
  expiry?: string
  registrar?: string
}

interface DnsRecord {
  name: string
  type: string
  ttl: number | null
  values: string[]
}

interface S3BucketItem {
  name: string
  region: string
  creation_date: string | null
  versioning: string
  public_access_blocked: boolean
}

interface S3ApiResponse {
  tenant: { id: number; name: string; environment: string }
  buckets: S3BucketItem[]
}

interface SecretItem {
  name: string
  arn: string
  description: string
  last_changed: string | null
  rotation_enabled: boolean
  region?: string
}

interface SecretsApiResponse {
  tenant: { id: number; name: string; environment: string }
  secrets: SecretItem[]
}

interface Ec2ApiResponse {
  tenant: { id: number; name: string; environment: string }
  instances: EC2Instance[]
}
interface ElbApiResponse {
  tenant: { id: number; name: string; environment: string }
  load_balancers: ElbItem[]
}
interface DomainsApiResponse {
  tenant: { id: number; name: string; environment: string }
  domains: DomainItem[]
}

interface NetworkValidation {
  port: number
  protocol: string
  container: string
  os_listening: boolean
  sg_open: boolean
  status: 'OK' | 'BLOCKED' | 'CONTAINER_DOWN' | 'UNREACHABLE'
  severity: 'ok' | 'error' | 'warning'
}

interface NetworkInterface {
  name: string
  rx_bytes_sec: number
  tx_bytes_sec: number
  rx_total_bytes: number
  tx_total_bytes: number
}

interface SgRule {
  sg_id: string
  sg_name: string
  protocol: string
  from_port: number | null
  to_port: number | null
  sources: string[]
}

interface OsPort {
  port: number
  protocol: string
  process: string
}

interface NetworkInfo {
  instance_id: string
  validation: NetworkValidation[]
  traffic: { interfaces: NetworkInterface[] }
  sg_rules: SgRule[]
  os_ports: OsPort[]
}

interface DiskInfo {
  filesystem: string
  mount: string
  total_kb: number
  used_kb: number
  avail_kb: number
  pct: number
}

interface ContainerHealthStat {
  name: string
  cpu_pct: string
  mem_used_mb: number
  mem_total_mb: number
  mem_pct: string
  restarts: number
}

interface EbsVolume {
  volume_id: string
  size_gb: number
  type: string
  state: string
  iops: number | null
  throughput: number | null
  encrypted: boolean
  device: string
  attach_state: string
}

interface HealthInfo {
  instance_id: string
  disks: DiskInfo[]
  swap_total_kb: number
  swap_used_kb: number
  load_avg: [number, number, number]
  uptime_s: number
  containers: ContainerHealthStat[]
  volumes: EbsVolume[]
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const TRANSITIONAL_STATES = new Set(['pending', 'stopping', 'shutting-down', 'rebooting'])

// ── Sort ────────────────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc'
type SortState<T extends string> = { col: T; dir: SortDir } | null

function useSortable<T extends string>() {
  const [sort, setSort] = useState<SortState<T>>(null)
  const toggle = (col: T) =>
    setSort(prev =>
      prev?.col !== col ? { col, dir: 'asc' }
      : prev.dir === 'asc' ? { col, dir: 'desc' }
      : null
    )
  return { sort, toggle }
}

function filterItems<T>(items: T[], text: string, fields: (item: T) => string[]) {
  if (!text.trim()) return items
  const q = text.toLowerCase()
  return items.filter(item => fields(item).some(f => f.toLowerCase().includes(q)))
}

function FilterInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <TextInput
      size="xs"
      placeholder="Filtrar..."
      leftSection={<IconSearch size={13} />}
      rightSection={value ? (
        <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => onChange('')}>
          <IconX size={11} />
        </ActionIcon>
      ) : null}
      value={value}
      onChange={e => onChange(e.currentTarget.value)}
      style={{ width: 220 }}
    />
  )
}

function sortItems<T>(items: T[], sort: SortState<string>, key: (col: string, item: T) => string | number) {
  if (!sort) return items
  return [...items].sort((a, b) => {
    const va = key(sort.col, a)
    const vb = key(sort.col, b)
    const cmp = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR', { sensitivity: 'base' })
    return sort.dir === 'asc' ? cmp : -cmp
  })
}

function SortableTh<T extends string>({
  col, sort, onToggle, children, style,
}: {
  col: T
  sort: SortState<T>
  onToggle: (col: T) => void
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  const active = sort?.col === col
  const Icon = !active ? IconArrowsUpDown : sort!.dir === 'asc' ? IconArrowUp : IconArrowDown
  return (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={() => onToggle(col)}
    >
      <Group gap={4} wrap="nowrap">
        {children}
        <Icon size={12} opacity={active ? 0.9 : 0.35} />
      </Group>
    </Table.Th>
  )
}

function statusColor(status?: string): string {
  const s = (status ?? '').toLowerCase()
  if (['running', 'active', 'available'].includes(s)) return 'green'
  if (['stopped', 'failed', 'terminated', 'error'].includes(s)) return 'red'
  if (TRANSITIONAL_STATES.has(s)) return 'orange'
  return 'gray'
}

// ── Action Modal (restart e force-restart compartilham o mesmo componente) ─────

type ActionMode = 'restart' | 'force-restart'

const ACTION_CONFIG: Record<ActionMode, {
  title: string
  endpoint: string
  confirmWord: string
  confirmLabel: string
  buttonColor: string
  buttonLabel: string
  warningText?: string
}> = {
  'restart': {
    title: 'Confirmar reinício de instância',
    endpoint: '/inventory/ec2/restart',
    confirmWord: 'RESTART',
    confirmLabel: 'Digite "RESTART" para confirmar',
    buttonColor: 'orange',
    buttonLabel: 'Reiniciar',
  },
  'force-restart': {
    title: 'Confirmar reinício forçado (stop + start)',
    endpoint: '/inventory/ec2/force-restart',
    confirmWord: 'FORCERESTART',
    confirmLabel: 'Digite "FORCERESTART" para confirmar',
    buttonColor: 'red',
    buttonLabel: 'Force Restart',
    warningText: 'Atenção: a instância será parada (stop) e religada (start). Haverá downtime de 2–5 minutos e o IP público pode mudar.',
  },
}

interface ActionModalProps {
  mode: ActionMode
  instance: EC2Instance | null
  tenantId: number
  onClose: () => void
  onSuccess: (instanceId: string) => void
}

function ActionModal({ mode, instance, tenantId, onClose, onSuccess }: ActionModalProps) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const cfg = ACTION_CONFIG[mode]

  type MutateVars = { inst: EC2Instance; reason: string }

  const mutation = useMutation({
    mutationFn: ({ inst, reason: r }: MutateVars) =>
      api.post(`/v1/tenants/${tenantId}${cfg.endpoint}`, {
        instance_id: inst.instance_id,
        reason: r,
        confirmation: true,
      }),
    onSuccess: (_data, { inst }) => {
      notifications.show({
        color: mode === 'restart' ? 'green' : 'orange',
        title: mode === 'restart' ? 'Reinício solicitado' : 'Force restart iniciado',
        message: mode === 'restart'
          ? `Instância ${inst.instance_id} será reiniciada em instantes.`
          : `Instância ${inst.instance_id} está sendo parada. O start ocorrerá automaticamente após o stop.`,
      })
      onSuccess(inst.instance_id)
      onClose()
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['inventory-ec2', tenantId] })
      }, 300)
    },
    onError: (err: unknown) => {
      const res = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const message =
        typeof res === 'string'
          ? res
          : Array.isArray(res)
            ? (res as Array<{ msg?: string }>).map(e => e.msg ?? JSON.stringify(e)).join(' | ')
            : 'Erro desconhecido.'
      notifications.show({ color: 'red', title: 'Erro ao executar operação', message })
    },
  })

  const handleClose = () => {
    if (mutation.isPending) return
    setReason('')
    setConfirmation('')
    mutation.reset()
    onClose()
  }

  const canConfirm = reason.trim().length >= 10 && confirmation === cfg.confirmWord

  return (
    <Modal
      opened={!!instance}
      onClose={handleClose}
      title={cfg.title}
      centered
      keepMounted={false}
    >
      {instance && (
        <Stack gap="sm">
          <Box>
            <Text size="sm"><strong>Nome:</strong> {instance.name ?? '—'}</Text>
            <Text size="sm" ff="monospace"><strong>ID:</strong> {instance.instance_id}</Text>
            <Text size="sm"><strong>Tipo:</strong> {instance.instance_type}</Text>
          </Box>

          {cfg.warningText && (
            <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light" p="xs">
              <Text size="xs">{cfg.warningText}</Text>
            </Alert>
          )}

          <Textarea
            label="Motivo"
            description="Mínimo 10 caracteres"
            placeholder="Descreva o motivo da operação..."
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            required
          />

          <TextInput
            label={cfg.confirmLabel}
            placeholder={cfg.confirmWord}
            value={confirmation}
            onChange={(e) => setConfirmation(e.currentTarget.value)}
            required
          />

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={handleClose} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button
              color={cfg.buttonColor}
              loading={mutation.isPending}
              disabled={!canConfirm}
              onClick={() => mutation.mutate({ inst: instance, reason })}
            >
              {cfg.buttonLabel}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

// ── Sub-tables ─────────────────────────────────────────────────────────────────

interface EC2TableProps {
  items: EC2Instance[]
  onRestart: (inst: EC2Instance) => void
  onForceRestart: (inst: EC2Instance) => void
  onDocker: (inst: EC2Instance) => void
  onDeploy: (inst: EC2Instance) => void
  onMetrics: (inst: EC2Instance) => void
  onNetwork: (inst: EC2Instance) => void
  onHealth: (inst: EC2Instance) => void
  onFiles: (inst: EC2Instance) => void
  restartingIds: Set<string>
  forceRestartingIds: Set<string>
}

type EC2Col = 'name' | 'type' | 'state' | 'region' | 'private_ip' | 'public_ip'

function EC2Table({ items, onRestart, onForceRestart, onDocker, onDeploy, onMetrics, onNetwork, onHealth, onFiles, restartingIds, forceRestartingIds }: EC2TableProps) {
  const { sort, toggle } = useSortable<EC2Col>()
  const [filter, setFilter] = useState('')
  const filtered = useMemo(() => filterItems(items, filter, inst => [
    inst.name ?? '', inst.instance_type, inst.state, inst.region ?? '',
    inst.private_ip ?? '', inst.public_ip ?? '',
  ]), [items, filter])
  const sorted = useMemo(() => sortItems(filtered, sort, (col, inst) => ({
    name: inst.name ?? '',
    type: inst.instance_type,
    state: inst.state,
    region: inst.region ?? '',
    private_ip: inst.private_ip ?? '',
    public_ip: inst.public_ip ?? '',
  }[col] ?? '')), [filtered, sort])

  if (items.length === 0) {
    return <Text c="dimmed" size="sm" py="md">Nenhum recurso encontrado.</Text>
  }
  return (
    <Stack gap="xs">
    <FilterInput value={filter} onChange={setFilter} />
    {sorted.length === 0 ? (
      <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{filter}".</Text>
    ) : (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <SortableTh col="name" sort={sort} onToggle={toggle}>Nome</SortableTh>
          <SortableTh col="type" sort={sort} onToggle={toggle}>Tipo</SortableTh>
          <SortableTh col="state" sort={sort} onToggle={toggle}>Estado</SortableTh>
          <SortableTh col="region" sort={sort} onToggle={toggle}>Região</SortableTh>
          <SortableTh col="private_ip" sort={sort} onToggle={toggle}>IP Privado</SortableTh>
          <SortableTh col="public_ip" sort={sort} onToggle={toggle}>IP Público</SortableTh>
          <Table.Th>Ações</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sorted.map((inst) => {
          const isRunning = inst.state === 'running'
          const isStopped = inst.state === 'stopped'
          const isRestarting = restartingIds.has(inst.instance_id)
          const isForceRestarting = forceRestartingIds.has(inst.instance_id)
          const isBusy = isRestarting || isForceRestarting

          return (
            <Table.Tr key={inst.instance_id}>
              <Table.Td>{inst.name ?? '—'}</Table.Td>
              <Table.Td>{inst.instance_type}</Table.Td>
              <Table.Td>
                {isRestarting ? (
                  <Group gap={6} wrap="nowrap">
                    <Loader size={12} color="orange" />
                    <Badge size="sm" color="orange" variant="light">reiniciando</Badge>
                  </Group>
                ) : isForceRestarting ? (
                  <Group gap={6} wrap="nowrap">
                    <Loader size={12} color="red" />
                    <Badge size="sm" color="red" variant="light">force restart</Badge>
                  </Group>
                ) : (
                  <Badge size="sm" color={statusColor(inst.state)} variant="light">
                    {inst.state}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{inst.region ?? '—'}</Table.Td>
              <Table.Td><Text size="xs" ff="monospace">{inst.private_ip ?? '—'}</Text></Table.Td>
              <Table.Td><Text size="xs" ff="monospace">{inst.public_ip ?? '—'}</Text></Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  <Tooltip
                    label={
                      isBusy ? 'Operação em andamento'
                      : isRunning ? 'Reiniciar (soft reboot)'
                      : 'Apenas instâncias running podem ser reiniciadas'
                    }
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      color="orange"
                      size="sm"
                      disabled={!isRunning || isBusy}
                      onClick={() => onRestart(inst)}
                    >
                      <IconRefresh size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip
                    label={
                      isBusy ? 'Operação em andamento'
                      : isRunning || isStopped ? 'Force restart (stop + start)'
                      : 'Disponível apenas para instâncias running ou stopped'
                    }
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      disabled={(!isRunning && !isStopped) || isBusy}
                      onClick={() => onForceRestart(inst)}
                    >
                      <IconPower size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isRunning ? 'Ver containers Docker' : 'Apenas instâncias running'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="sm"
                      disabled={!isRunning}
                      onClick={() => onDocker(inst)}
                    >
                      <IconBrandDocker size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isRunning ? 'Métricas (CPU / RAM / Disco)' : 'Apenas instâncias running'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="teal"
                      size="sm"
                      disabled={!isRunning}
                      onClick={() => onMetrics(inst)}
                    >
                      <IconDeviceDesktopAnalytics size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isRunning ? 'Deploy (deploy.sh)' : 'Apenas instâncias running'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="violet"
                      size="sm"
                      disabled={!isRunning || isBusy}
                      onClick={() => onDeploy(inst)}
                    >
                      <IconRocket size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isRunning ? 'Rede (portas, tráfego, SG)' : 'Apenas instâncias running'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="cyan"
                      size="sm"
                      disabled={!isRunning}
                      onClick={() => onNetwork(inst)}
                    >
                      <IconNetwork size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isRunning ? 'Health (disco, docker, EBS)' : 'Apenas instâncias running'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="green"
                      size="sm"
                      disabled={!isRunning}
                      onClick={() => onHealth(inst)}
                    >
                      <IconActivity size={14} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isRunning ? 'Arquivos' : 'Apenas instâncias running'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="yellow"
                      size="sm"
                      disabled={!isRunning}
                      onClick={() => onFiles(inst)}
                    >
                      <IconFolder size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
    )}
    </Stack>
  )
}

function targetHealthColor(status: string): string {
  const s = status.toLowerCase()
  if (s === 'healthy') return 'green'
  if (s === 'unhealthy') return 'red'
  if (['draining', 'initial', 'unavailable'].includes(s)) return 'orange'
  return 'gray'
}

function ElbTargetsDrawer({ elb, onClose }: { elb: ElbItem; onClose: () => void }) {
  const tgs = elb.target_groups ?? []
  return (
    <ResizableDrawer
      storageKey="elb-targets-drawer-width"
      defaultWidth={600}
      minWidth={400}
      opened
      onClose={onClose}
      title={<Text fw={600} size="sm">{elb.name} — Target Groups</Text>}
      position="right"
      padding="md"
    >
      <ScrollArea h="calc(100vh - 80px)">
        {tgs.length === 0 ? (
          <Text c="dimmed" size="sm">Nenhum target group encontrado.</Text>
        ) : (
          <Stack gap="lg">
            {tgs.map((tg, i) => (
              <Box key={i}>
                <Group gap="xs" mb="xs">
                  <IconServer size={14} />
                  <Text fw={600} size="sm">{tg.name}</Text>
                  <Badge size="xs" variant="outline" color="gray">
                    {tg.protocol}:{tg.port}
                  </Badge>
                  <Badge size="xs" variant="light" color="blue">
                    {tg.targets.length} target{tg.targets.length !== 1 ? 's' : ''}
                  </Badge>
                </Group>
                {(tg.hc_protocol || tg.hc_path) && (
                  <Text size="xs" c="dimmed" mb="xs" pl={22} ff="monospace">
                    Health check:{' '}
                    <Text span c="dimmed">
                      {tg.hc_protocol}{tg.hc_port && tg.hc_port !== 'traffic-port' ? `:${tg.hc_port}` : ''}
                      {tg.hc_path ? tg.hc_path : ''}
                    </Text>
                  </Text>
                )}
                {tg.targets.length === 0 ? (
                  <Text c="dimmed" size="xs" pl="sm">Nenhum target registrado.</Text>
                ) : (
                  <Table withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Target</Table.Th>
                        <Table.Th>Porta</Table.Th>
                        <Table.Th>Health</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {tg.targets.map((t, j) => (
                        <Table.Tr key={j}>
                          <Table.Td>
                            {t.target_name ? (
                              <Tooltip label={t.target_id} position="right" withArrow>
                                <Text size="xs" style={{ cursor: 'default' }}>{t.target_name}</Text>
                              </Tooltip>
                            ) : (
                              <Text size="xs" ff="monospace">{t.target_id}</Text>
                            )}
                          </Table.Td>
                          <Table.Td><Text size="xs">{t.port}</Text></Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={targetHealthColor(t.health_status)} variant="light">
                              {t.health_status}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                {i < tgs.length - 1 && <Divider mt="md" />}
              </Box>
            ))}
          </Stack>
        )}
      </ScrollArea>
    </ResizableDrawer>
  )
}

type ElbCol = 'name' | 'dns_name' | 'type' | 'scheme' | 'state' | 'region'

function ElbTable({ items }: { items: ElbItem[] }) {
  const { sort, toggle } = useSortable<ElbCol>()
  const [filter, setFilter] = useState('')
  const [drawerElb, setDrawerElb] = useState<ElbItem | null>(null)
  const filtered = useMemo(() => filterItems(items, filter, elb => [
    elb.name ?? '', elb.dns_name ?? '', elb.type ?? '', elb.scheme ?? '',
    elb.state ?? '', elb.region ?? '',
  ]), [items, filter])
  const sorted = useMemo(() => sortItems(filtered, sort, (col, elb) => ({
    name: elb.name ?? '',
    dns_name: elb.dns_name ?? '',
    type: elb.type ?? '',
    scheme: elb.scheme ?? '',
    state: elb.state ?? '',
    region: elb.region ?? '',
  }[col] ?? '')), [filtered, sort])

  if (items.length === 0) {
    return <Text c="dimmed" size="sm" py="md">Nenhum recurso encontrado.</Text>
  }
  return (
    <Stack gap="xs">
    {drawerElb && <ElbTargetsDrawer elb={drawerElb} onClose={() => setDrawerElb(null)} />}
    <FilterInput value={filter} onChange={setFilter} />
    {sorted.length === 0 ? (
      <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{filter}".</Text>
    ) : (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <SortableTh col="name" sort={sort} onToggle={toggle}>Nome</SortableTh>
          <SortableTh col="dns_name" sort={sort} onToggle={toggle}>DNS</SortableTh>
          <SortableTh col="type" sort={sort} onToggle={toggle}>Tipo</SortableTh>
          <SortableTh col="scheme" sort={sort} onToggle={toggle}>Scheme</SortableTh>
          <SortableTh col="state" sort={sort} onToggle={toggle}>Estado</SortableTh>
          <SortableTh col="region" sort={sort} onToggle={toggle}>Região</SortableTh>
          <Table.Th>Listeners</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sorted.map((elb, i) => (
          <Table.Tr key={i}>
            <Table.Td fw={500}>{elb.name}</Table.Td>
            <Table.Td><Text size="xs" ff="monospace">{elb.dns_name ?? '—'}</Text></Table.Td>
            <Table.Td>{elb.type ?? elb.lb_type ?? '—'}</Table.Td>
            <Table.Td>{elb.scheme ?? '—'}</Table.Td>
            <Table.Td>
              <Badge size="sm" color={statusColor(elb.state)} variant="light">
                {elb.state ?? '—'}
              </Badge>
            </Table.Td>
            <Table.Td>{elb.region ?? '—'}</Table.Td>
            <Table.Td>
              <Group gap={4} wrap="wrap">
                {(elb.listeners ?? []).map((l, j) => (
                  <Badge key={j} size="xs" variant="outline" color="indigo">
                    {l.protocol}:{l.port}
                  </Badge>
                ))}
                {(elb.target_groups ?? []).length > 0 && (
                  <Tooltip label="Ver targets e health status">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="blue"
                      onClick={() => setDrawerElb(elb)}
                    >
                      <IconServer size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
    )}
    </Stack>
  )
}

// ── S3 Bucket Drawer ────────────────────────────────────────────────────────────

interface S3ObjectItem {
  key: string
  size: number
  last_modified: string | null
  storage_class: string
  is_prefix: boolean
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

const S3_PAGE_SIZE = 50

function S3BucketDrawer({ bucket, tenantId, onClose }: { bucket: S3BucketItem; tenantId: number; onClose: () => void }) {
  const qc = useQueryClient()
  const [prefix, setPrefix] = useState('')
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const uploadRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, error } = useQuery<{ bucket: string; prefix: string; objects: S3ObjectItem[]; total: number; page: number; page_size: number }, Error>({
    queryKey: ['s3-objects', tenantId, bucket.name, prefix, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(S3_PAGE_SIZE) })
      if (prefix) params.set('prefix', prefix)
      const res = await api.get<{ bucket: string; prefix: string; objects: S3ObjectItem[]; total: number; page: number; page_size: number }>(
        `/v1/tenants/${tenantId}/inventory/s3/${encodeURIComponent(bucket.name)}/objects?${params}`
      )
      return res.data
    },
    staleTime: 30_000,
  })

  const objects = data?.objects ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / S3_PAGE_SIZE))

  const filtered = useMemo(() => {
    if (!filter) return objects
    const q = filter.toLowerCase()
    return objects.filter(o => o.key.toLowerCase().includes(q))
  }, [objects, filter])

  const selectableKeys = filtered.filter(o => !o.is_prefix).map(o => o.key)
  const allSelected = selectableKeys.length > 0 && selectableKeys.every(k => selected.has(k))
  const someSelected = selected.size > 0

  const breadcrumbs = useMemo(() => {
    if (!prefix) return []
    return prefix.replace(/\/$/, '').split('/').map((part, i, arr) => ({
      label: part,
      prefix: arr.slice(0, i + 1).join('/') + '/',
    }))
  }, [prefix])

  const navigateTo = (newPrefix: string) => {
    setPrefix(newPrefix)
    setFilter('')
    setPage(1)
    setSelected(new Set())
  }

  const toggleSelect = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const toggleAll = () => {
    if (allSelected) {
      setSelected(prev => { const next = new Set(prev); selectableKeys.forEach(k => next.delete(k)); return next })
    } else {
      setSelected(prev => new Set([...prev, ...selectableKeys]))
    }
  }

  const handleDownload = async () => {
    const keys = [...selected].filter(k => !k.endsWith('/'))
    for (const key of keys) {
      try {
        const res = await api.get<{ url: string }>(
          `/v1/tenants/${tenantId}/inventory/s3/${encodeURIComponent(bucket.name)}/presign?key=${encodeURIComponent(key)}`
        )
        const a = document.createElement('a')
        a.href = res.data.url
        a.download = key.split('/').pop() ?? key
        a.target = '_blank'
        a.click()
      } catch {
        notifications.show({ color: 'red', message: `Erro ao gerar link para ${key}` })
      }
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    const keys = [...selected]
    let errors = 0
    for (const key of keys) {
      try {
        await api.delete(`/v1/tenants/${tenantId}/inventory/s3/${encodeURIComponent(bucket.name)}/objects?key=${encodeURIComponent(key)}`)
      } catch {
        errors++
      }
    }
    setDeleting(false)
    setDeleteConfirm(false)
    setSelected(new Set())
    qc.invalidateQueries({ queryKey: ['s3-objects', tenantId, bucket.name] })
    if (errors > 0) {
      notifications.show({ color: 'red', message: `${errors} objeto(s) não puderam ser deletados.` })
    } else {
      notifications.show({ color: 'green', message: `${keys.length} objeto(s) deletado(s).` })
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    setUploading(true)
    let errors = 0
    for (const file of files) {
      try {
        const form = new FormData()
        form.append('file', file)
        await api.post(
          `/v1/tenants/${tenantId}/inventory/s3/${encodeURIComponent(bucket.name)}/upload-file?prefix=${encodeURIComponent(prefix)}`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
      } catch {
        errors++
      }
    }
    setUploading(false)
    if (uploadRef.current) uploadRef.current.value = ''
    qc.invalidateQueries({ queryKey: ['s3-objects', tenantId, bucket.name] })
    if (errors > 0) {
      notifications.show({ color: 'red', message: `${errors} arquivo(s) não puderam ser enviados.` })
    } else {
      notifications.show({ color: 'green', message: `${files.length} arquivo(s) enviado(s).` })
    }
  }

  return (
    <ResizableDrawer
      storageKey="s3-drawer-width"
      defaultWidth={700}
      minWidth={500}
      opened
      onClose={onClose}
      title={<Text fw={600} size="sm">{bucket.name}</Text>}
      position="right"
      padding="md"
    >
      <ScrollArea h="calc(100vh - 80px)">
        <Stack gap="sm">
          {/* Detalhes */}
          <Group gap="xs">
            <Badge size="sm" variant="light" color="blue">{bucket.region}</Badge>
            <Badge size="sm" variant="light" color={bucket.versioning === 'Enabled' ? 'green' : bucket.versioning === 'Suspended' ? 'yellow' : 'gray'}>
              Versioning: {bucket.versioning}
            </Badge>
            <Badge size="sm" variant="light" color={bucket.public_access_blocked ? 'green' : 'red'}>
              {bucket.public_access_blocked ? 'Acesso bloqueado' : 'Acesso público'}
            </Badge>
            {bucket.creation_date && (
              <Text size="xs" c="dimmed">Criado em {new Date(bucket.creation_date).toLocaleDateString('pt-BR')}</Text>
            )}
          </Group>

          <Divider />

          {/* Breadcrumb */}
          <Group gap={4}>
            <Text size="xs" c="blue" style={{ cursor: 'pointer' }} onClick={() => navigateTo('')}>{bucket.name}</Text>
            {breadcrumbs.map((bc, i) => (
              <Group key={i} gap={4}>
                <Text size="xs" c="dimmed">/</Text>
                <Text
                  size="xs"
                  c={i === breadcrumbs.length - 1 ? 'gray' : 'blue'}
                  style={{ cursor: i === breadcrumbs.length - 1 ? 'default' : 'pointer' }}
                  onClick={() => i < breadcrumbs.length - 1 && navigateTo(bc.prefix)}
                >{bc.label}</Text>
              </Group>
            ))}
          </Group>

          {/* Barra de ações */}
          <Group justify="space-between">
            <Group gap="xs">
              {someSelected && (
                <>
                  <Text size="xs" c="dimmed">{selected.size} selecionado(s)</Text>
                  <Button size="xs" variant="light" color="blue" onClick={handleDownload}>Download</Button>
                  <Button size="xs" variant="light" color="red" onClick={() => setDeleteConfirm(true)}>Deletar</Button>
                </>
              )}
            </Group>
            <Group gap="xs">
              <input ref={uploadRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
              <Button size="xs" variant="light" color="green" loading={uploading} onClick={() => uploadRef.current?.click()}>
                Upload
              </Button>
            </Group>
          </Group>

          <FilterInput value={filter} onChange={setFilter} />

          {/* Modal de confirmação de delete */}
          {deleteConfirm && (
            <Alert color="red" variant="light">
              <Stack gap="xs">
                <Text size="sm">Deletar {selected.size} objeto(s)? Esta ação não pode ser desfeita.</Text>
                <Group gap="xs">
                  <Button size="xs" color="red" loading={deleting} onClick={handleDelete}>Confirmar</Button>
                  <Button size="xs" variant="default" onClick={() => setDeleteConfirm(false)}>Cancelar</Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {isLoading && <Loader size="sm" />}
          {error && <Text c="red" size="sm">Erro ao buscar objetos: {error.message}</Text>}
          {!isLoading && !error && filtered.length === 0 && (
            <Text c="dimmed" size="sm">Nenhum objeto encontrado.</Text>
          )}
          {!isLoading && filtered.length > 0 && (
            <Table withTableBorder withColumnBorders striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36}>
                    <Checkbox
                      size="xs"
                      checked={allSelected}
                      indeterminate={!allSelected && selectableKeys.some(k => selected.has(k))}
                      onChange={toggleAll}
                    />
                  </Table.Th>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>Tamanho</Table.Th>
                  <Table.Th>Modificado em</Table.Th>
                  <Table.Th>Storage Class</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((obj, i) => {
                  const displayName = obj.key.replace(prefix, '')
                  const isSelected = selected.has(obj.key)
                  return (
                    <Table.Tr key={i} bg={isSelected ? 'blue.0' : undefined}>
                      <Table.Td>
                        {!obj.is_prefix && (
                          <Checkbox size="xs" checked={isSelected} onChange={() => toggleSelect(obj.key)} onClick={e => e.stopPropagation()} />
                        )}
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: obj.is_prefix ? 'pointer' : 'default' }}
                        onClick={() => obj.is_prefix && navigateTo(obj.key)}
                      >
                        {obj.is_prefix ? (
                          <Text size="xs" c="blue" ff="monospace">📁 {displayName}</Text>
                        ) : (
                          <Text size="xs" ff="monospace">{displayName}</Text>
                        )}
                      </Table.Td>
                      <Table.Td><Text size="xs">{obj.is_prefix ? '—' : formatBytes(obj.size)}</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs">{obj.last_modified ? new Date(obj.last_modified).toLocaleDateString('pt-BR') : '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        {obj.storage_class
                          ? <Badge size="xs" variant="outline" color="gray">{obj.storage_class}</Badge>
                          : <Text size="xs">—</Text>}
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <Group justify="space-between" mt="xs">
              <Text size="xs" c="dimmed">{total} objetos — página {page} de {totalPages}</Text>
              <Group gap="xs">
                <Button size="xs" variant="default" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                <Button size="xs" variant="default" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
              </Group>
            </Group>
          )}
        </Stack>
      </ScrollArea>
    </ResizableDrawer>
  )
}

// ── S3 Create Bucket Modal ───────────────────────────────────────────────────

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
]

const BUCKET_NAME_RE = /^[a-z0-9][a-z0-9.\-]{1,61}[a-z0-9]$/
const IP_RE = /^\d+\.\d+\.\d+\.\d+$/

function validateBucketName(name: string): string | null {
  if (!name) return 'Nome obrigatório'
  if (name.length < 3) return 'Mínimo 3 caracteres'
  if (name.length > 63) return 'Máximo 63 caracteres'
  if (!BUCKET_NAME_RE.test(name)) return 'Apenas letras minúsculas, números, pontos e hífens'
  if (IP_RE.test(name)) return 'Nome não pode ser um endereço IP'
  if (name.includes('..') || name.includes('.-') || name.includes('-.')) return 'Sequencias inválidas (.. .- -.)'
  return null
}

function CreateBucketModal({ opened, onClose, tenantId }: { opened: boolean; onClose: () => void; tenantId: number }) {
  const [name, setName] = useState('')
  const [region, setRegion] = useState('us-east-1')
  const [creating, setCreating] = useState(false)
  const qc = useQueryClient()

  const nameError = name ? validateBucketName(name) : null

  const handleCreate = async () => {
    if (validateBucketName(name)) return
    setCreating(true)
    try {
      await api.post(`/v1/tenants/${tenantId}/inventory/s3`, { name, region })
      notifications.show({ color: 'green', message: `Bucket "${name}" criado com sucesso.` })
      qc.invalidateQueries({ queryKey: ['s3-buckets', tenantId] })
      setName('')
      setRegion('us-east-1')
      onClose()
    } catch (e: any) {
      const rawDetail = e?.response?.data?.detail
      const detail = typeof rawDetail === 'string' ? rawDetail : ''
      if (detail === 'bucket_already_exists') {
        notifications.show({ color: 'red', message: 'Esse nome de bucket já existe na AWS.' })
      } else if (detail === 'invalid_bucket_name') {
        notifications.show({ color: 'red', message: 'Nome de bucket inválido.' })
      } else if (detail === 'prd_read_only') {
        notifications.show({ color: 'red', message: 'Criação bloqueada em ambiente de produção.' })
      } else if (detail === 'insufficient_role_permission') {
        notifications.show({ color: 'red', message: 'Sem permissão. Apenas admin e devops podem criar buckets.' })
      } else {
        notifications.show({ color: 'red', message: `Erro ao criar bucket: ${formatApiError(e, 'erro desconhecido')}` })
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="sm">Criar Bucket S3</Text>}
      centered
    >
      <Stack gap="md">
        <TextInput
          label="Nome do bucket"
          placeholder="meu-bucket-exemplo"
          value={name}
          onChange={e => setName(e.currentTarget.value.toLowerCase())}
          error={nameError}
          description="Letras minúsculas, números, pontos e hífens. 3-63 caracteres."
        />
        <Select
          label="Região"
          data={AWS_REGIONS}
          value={region}
          onChange={v => setRegion(v ?? 'us-east-1')}
        />
        <Alert variant="light" color="teal" icon={<IconAlertCircle size={14} />}>
          <Text size="xs">Segurança aplicada automaticamente: Versioning ativado, acesso público bloqueado, encryption AES-256.</Text>
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button
            color="teal"
            loading={creating}
            disabled={!name || !!nameError}
            onClick={handleCreate}
          >
            Criar Bucket
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── S3 Table ────────────────────────────────────────────────────────────────────

type S3Col = 'name' | 'region' | 'versioning'

function S3Table({ items, tenantId }: { items: S3BucketItem[]; tenantId: number }) {
  const { sort, toggle } = useSortable<S3Col>()
  const [filter, setFilter] = useState('')
  const [selectedBucket, setSelectedBucket] = useState<S3BucketItem | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const filtered = useMemo(() => filterItems(items, filter, b => [
    b.name, b.region, b.versioning, b.public_access_blocked ? 'bloqueado' : 'público',
  ]), [items, filter])
  const sorted = useMemo(() => sortItems(filtered, sort, (col, b) => ({
    name: b.name,
    region: b.region,
    versioning: b.versioning,
  }[col] ?? '')), [filtered, sort])

  if (items.length === 0) {
    return <Text c="dimmed" size="sm" py="md">Nenhum bucket encontrado.</Text>
  }
  return (
    <Stack gap="xs">
      {selectedBucket && (
        <S3BucketDrawer bucket={selectedBucket} tenantId={tenantId} onClose={() => setSelectedBucket(null)} />
      )}
      <CreateBucketModal opened={createOpen} onClose={() => setCreateOpen(false)} tenantId={tenantId} />
      <Group justify="space-between">
        <FilterInput value={filter} onChange={setFilter} />
        <Button size="xs" variant="light" color="teal" leftSection={<IconRocket size={14} />} onClick={() => setCreateOpen(true)}>
          Criar Bucket
        </Button>
      </Group>
      {sorted.length === 0 ? (
        <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{filter}".</Text>
      ) : (
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <SortableTh col="name" sort={sort} onToggle={toggle}>Nome</SortableTh>
              <SortableTh col="region" sort={sort} onToggle={toggle}>Região</SortableTh>
              <SortableTh col="versioning" sort={sort} onToggle={toggle}>Versioning</SortableTh>
              <Table.Th>Acesso Público</Table.Th>
              <Table.Th>Criado em</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((b, i) => (
              <Table.Tr key={i} style={{ cursor: 'pointer' }} onClick={() => setSelectedBucket(b)}>
                <Table.Td fw={500}><Text size="xs" ff="monospace">{b.name}</Text></Table.Td>
                <Table.Td><Text size="xs">{b.region}</Text></Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={b.versioning === 'Enabled' ? 'green' : b.versioning === 'Suspended' ? 'yellow' : 'gray'}>
                    {b.versioning}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={b.public_access_blocked ? 'green' : 'red'}>
                    {b.public_access_blocked ? 'Bloqueado' : 'Público'}
                  </Badge>
                </Table.Td>
                <Table.Td><Text size="xs">{b.creation_date ? new Date(b.creation_date).toLocaleDateString('pt-BR') : '—'}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}

// ── Secrets Manager Table ───────────────────────────────────────────────────────

type SecretCol = 'name' | 'region' | 'last_changed'

function SecretsTable({ items, onSelect }: { items: SecretItem[]; onSelect: (s: SecretItem) => void }) {
  const { sort, toggle } = useSortable<SecretCol>()
  const [filter, setFilter] = useState('')
  const filtered = useMemo(() => filterItems(items, filter, s => [
    s.name, s.arn, s.description, s.rotation_enabled ? 'ativo' : 'desativado', s.region ?? '',
  ]), [items, filter])
  const sorted = useMemo(() => sortItems(filtered, sort, (col, s) => ({
    name: s.name,
    region: s.region ?? '',
    last_changed: s.last_changed ?? '',
  }[col] ?? '')), [filtered, sort])

  if (items.length === 0) {
    return <Text c="dimmed" size="sm" py="md">Nenhum secret encontrado.</Text>
  }
  return (
    <Stack gap="xs">
      <FilterInput value={filter} onChange={setFilter} />
      {sorted.length === 0 ? (
        <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{filter}".</Text>
      ) : (
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <SortableTh col="name" sort={sort} onToggle={toggle}>Nome</SortableTh>
              <Table.Th>ARN</Table.Th>
              <Table.Th>Rotação</Table.Th>
              <SortableTh col="region" sort={sort} onToggle={toggle}>Região</SortableTh>
              <SortableTh col="last_changed" sort={sort} onToggle={toggle}>Última alteração</SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((s, i) => (
              <Table.Tr key={i} onClick={() => onSelect(s)} style={{ cursor: 'pointer' }}>
                <Table.Td fw={500}>{s.name}</Table.Td>
                <Table.Td>
                  <Tooltip label={s.arn} position="top" withArrow>
                    <Text size="xs" ff="monospace" style={{ cursor: 'pointer', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.arn}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={s.rotation_enabled ? 'green' : 'gray'}>
                    {s.rotation_enabled ? 'Ativo' : 'Desativado'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {s.region
                    ? <Badge size="sm" variant="light" color="blue">{s.region}</Badge>
                    : <Text size="xs" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{s.last_changed ? new Date(s.last_changed).toLocaleDateString('pt-BR') : '—'}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}

interface SecretKeyValue { key: string; value: string }

function SecretValueRow({ kv, editing, editValue, onEditChange }: {
  kv: SecretKeyValue
  editing: boolean
  editValue?: string
  onEditChange?: (key: string, value: string) => void
}) {
  const [visible, setVisible] = useState(false)
  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
    notifications.show({ title: 'Copiado', message: `Valor de "${kv.key}" copiado`, color: 'teal', autoClose: 2000 })
  }, [kv.key])

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" fw={500} ff="monospace">{kv.key}</Text>
      </Table.Td>
      <Table.Td>
        {editing ? (
          <TextInput
            size="xs"
            ff="monospace"
            value={editValue ?? kv.value}
            onChange={(e) => onEditChange?.(kv.key, e.currentTarget.value)}
            styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        ) : (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" ff="monospace" style={{ flex: 1, wordBreak: 'break-all' }}>
              {visible ? kv.value : '••••••••'}
            </Text>
            <ActionIcon variant="subtle" size="sm" onClick={() => setVisible(v => !v)} title={visible ? 'Ocultar' : 'Mostrar'}>
              {visible ? <IconEyeOff size={14} /> : <IconEye size={14} />}
            </ActionIcon>
            <ActionIcon variant="subtle" size="sm" onClick={() => copyToClipboard(kv.value)} title="Copiar">
              <IconCopy size={14} />
            </ActionIcon>
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  )
}

function SecretsDrawer({ secret, tenantId, onClose }: { secret: SecretItem; tenantId: number; onClose: () => void }) {
  const tenant = useCurrentTenant()
  const environment = tenant?.environment ?? ''
  const isPrd = ['prd', 'stg', 'hml'].includes(environment.toLowerCase())
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<{ secret_id: string; keys: SecretKeyValue[] }, Error>({
    queryKey: ['secret-value', tenantId, secret.name],
    queryFn: async () => {
      const res = await api.get<{ secret_id: string; keys: SecretKeyValue[] }>(
        `/v1/tenants/${tenantId}/inventory/secrets/${secret.name}`
      )
      return res.data
    },
    staleTime: 30_000,
  })

  const keys = data?.keys ?? []

  const [editing, setEditing] = useState(false)
  const [editValues, setEditValues] = useState<Record<string, string>>({})
  const [confirmOpen, setConfirmOpen] = useState(false)

  const startEdit = useCallback(() => {
    const initial: Record<string, string> = {}
    keys.forEach((kv) => { initial[kv.key] = kv.value })
    setEditValues(initial)
    setEditing(true)
  }, [keys])

  const cancelEdit = useCallback(() => {
    setEditing(false)
    setEditValues({})
  }, [])

  const handleEditChange = useCallback((key: string, value: string) => {
    setEditValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const hasChanges = useMemo(() => {
    return keys.some((kv) => editValues[kv.key] !== undefined && editValues[kv.key] !== kv.value)
  }, [keys, editValues])

  const buildSecretString = useCallback(() => {
    const obj: Record<string, string> = {}
    keys.forEach((kv) => { obj[kv.key] = editValues[kv.key] ?? kv.value })
    return JSON.stringify(obj)
  }, [keys, editValues])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.put(
        `/v1/tenants/${tenantId}/inventory/secrets/${secret.name}`,
        { secret_string: buildSecretString() },
      )
      return res.data
    },
    onSuccess: (result: any) => {
      notifications.show({
        title: 'Secret atualizado',
        message: result?.audited ? 'Alteração salva e registrada na auditoria.' : 'Alteração salva com sucesso.',
        color: 'teal',
        autoClose: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ['secret-value', tenantId, secret.name] })
      setEditing(false)
      setEditValues({})
    },
    onError: (err: any) => {
      const { title, message } = erroAoSalvarSecret(err)
      notifications.show({ title, message, color: 'red', autoClose: 8000 })
    },
  })

  const handleSave = useCallback(() => {
    if (isPrd) {
      setConfirmOpen(true)
    } else {
      saveMutation.mutate()
    }
  }, [isPrd, saveMutation])

  const confirmSave = useCallback(() => {
    setConfirmOpen(false)
    saveMutation.mutate()
  }, [saveMutation])

  return (
    <>
      <ResizableDrawer
        storageKey="secrets-drawer-width"
        defaultWidth={650}
        minWidth={450}
        opened
        onClose={onClose}
        title={<Text fw={600} size="sm">{secret.name}</Text>}
        position="right"
        padding="md"
      >
        <ScrollArea h="calc(100vh - 80px)">
          <Stack gap="md">
            <Box>
              <Text size="xs" c="dimmed">ARN</Text>
              <Code block style={{ fontSize: 11 }}>{secret.arn}</Code>
            </Box>
            <Group gap="lg">
              <Box>
                <Text size="xs" c="dimmed">Rotação</Text>
                <Badge size="sm" variant="light" color={secret.rotation_enabled ? 'green' : 'gray'}>
                  {secret.rotation_enabled ? 'Ativo' : 'Desativado'}
                </Badge>
              </Box>
              {secret.last_changed && (
                <Box>
                  <Text size="xs" c="dimmed">Última alteração</Text>
                  <Text size="sm">{new Date(secret.last_changed).toLocaleDateString('pt-BR')}</Text>
                </Box>
              )}
              {secret.description && (
                <Box>
                  <Text size="xs" c="dimmed">Descrição</Text>
                  <Text size="sm">{secret.description}</Text>
                </Box>
              )}
            </Group>
            <Divider />
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm">Chaves e Valores</Text>
              {!isLoading && !error && keys.length > 0 && !editing && (
                <Tooltip label="Editar valores">
                  <ActionIcon variant="subtle" color="teal" size="sm" onClick={startEdit}>
                    <IconPencil size={15} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            {isLoading && <Loader size="sm" />}
            {error && <Text c="red" size="sm">
              {error.message?.includes('502') || error.message?.includes('secret_fetch_failed')
                ? 'Sem permissão para ler este secret na conta AWS. Verifique a policy do cross-account role.'
                : `Erro ao buscar valores: ${error.message}`}
            </Text>}
            {!isLoading && !error && keys.length === 0 && (
              <Text c="dimmed" size="sm">Nenhuma chave encontrada.</Text>
            )}
            {!isLoading && keys.length > 0 && (
              <Table withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Chave</Table.Th>
                    <Table.Th>Valor</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {keys.map((kv) => (
                    <SecretValueRow
                      key={kv.key}
                      kv={kv}
                      editing={editing}
                      editValue={editValues[kv.key]}
                      onEditChange={handleEditChange}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {editing && (
              <Group justify="flex-end" gap="xs">
                <Button variant="default" size="xs" onClick={cancelEdit} disabled={saveMutation.isPending}>
                  Cancelar
                </Button>
                <Button
                  size="xs"
                  color="teal"
                  leftSection={<IconDeviceFloppy size={14} />}
                  onClick={handleSave}
                  loading={saveMutation.isPending}
                  disabled={!hasChanges}
                >
                  Salvar
                </Button>
              </Group>
            )}
          </Stack>
        </ScrollArea>
      </ResizableDrawer>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={18} color="var(--mantine-color-yellow-6)" />
            <Text fw={600} size="sm">Confirmar edição em {environment.toUpperCase()}</Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Você está editando um secret em ambiente <Badge size="sm" color="red" variant="light">{environment.toUpperCase()}</Badge>.
            Esta alteração será registrada na auditoria.
          </Text>
          <Text size="xs" c="dimmed">Secret: {secret.name}</Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button size="xs" color="red" onClick={confirmSave} loading={saveMutation.isPending}>
              Confirmar e salvar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

type DnsRecordCol = 'name' | 'type' | 'ttl'

function ZoneRecordsDrawer({ zone, tenantId, onClose }: { zone: DomainItem; tenantId: number; onClose: () => void }) {
  const zoneId = zone.zone_id ?? ''
  const zoneName = zone.name ?? zone.domain_name ?? zoneId
  const { data, isLoading, error } = useQuery<{ zone_id: string; records: DnsRecord[] }, Error>({
    queryKey: ['zone-records', tenantId, zoneId],
    queryFn: async () => {
      const res = await api.get<{ zone_id: string; records: DnsRecord[] }>(
        `/v1/tenants/${tenantId}/inventory/domains/${zoneId}/records`
      )
      return res.data
    },
    enabled: !!zoneId,
    staleTime: 60_000,
  })

  const [filter, setFilter] = useState('')
  const { sort, toggle } = useSortable<DnsRecordCol>()

  const records = data?.records ?? []
  const filtered = useMemo(() => filterItems(records, filter, r => [
    r.name, r.type, r.ttl != null ? `${r.ttl}` : '', ...r.values,
  ]), [records, filter])
  const sorted = useMemo(() => sortItems(filtered, sort, (col, r) => ({
    name: r.name,
    type: r.type,
    ttl: r.ttl ?? -1,
  }[col] ?? '')), [filtered, sort])

  return (
    <ResizableDrawer
      storageKey="dns-drawer-width"
      defaultWidth={700}
      minWidth={500}
      opened
      onClose={onClose}
      title={<Text fw={600} size="sm">{zoneName} — Records DNS</Text>}
      position="right"
      padding="md"
    >
      <ScrollArea h="calc(100vh - 80px)">
        {isLoading && <Loader size="sm" />}
        {error && <Text c="red" size="sm">Erro ao buscar records: {error.message}</Text>}
        {!isLoading && !error && records.length === 0 && (
          <Text c="dimmed" size="sm">Nenhum record encontrado.</Text>
        )}
        {!isLoading && records.length > 0 && (
          <Stack gap="xs">
            <FilterInput value={filter} onChange={setFilter} />
            {sorted.length === 0 ? (
              <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{filter}".</Text>
            ) : (
              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <SortableTh col="name" sort={sort} onToggle={toggle}>Nome</SortableTh>
                    <SortableTh col="type" sort={sort} onToggle={toggle}>Tipo</SortableTh>
                    <SortableTh col="ttl" sort={sort} onToggle={toggle}>TTL</SortableTh>
                    <Table.Th>Valor(es)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sorted.map((r, i) => (
                    <Table.Tr key={i}>
                      <Table.Td><Text size="xs" ff="monospace">{r.name}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color="blue">{r.type}</Badge>
                      </Table.Td>
                      <Table.Td><Text size="xs">{r.ttl != null ? `${r.ttl}s` : 'Alias'}</Text></Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          {r.values.map((v, j) => (
                            <Text key={j} size="xs" ff="monospace">{v}</Text>
                          ))}
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        )}
      </ScrollArea>
    </ResizableDrawer>
  )
}

type DomainCol = 'name' | 'type' | 'records' | 'zone_id'

function DomainTable({ items, tenantId }: { items: DomainItem[]; tenantId: number }) {
  const { sort, toggle } = useSortable<DomainCol>()
  const [filter, setFilter] = useState('')
  const [selectedZone, setSelectedZone] = useState<DomainItem | null>(null)
  const filtered = useMemo(() => filterItems(items, filter, d => [
    d.name ?? d.domain_name ?? '', d.private_zone ? 'Private' : 'Public',
    d.zone_id ?? '', String(d.record_set_count ?? ''),
  ]), [items, filter])
  const sorted = useMemo(() => sortItems(filtered, sort, (col, d) => ({
    name: d.name ?? d.domain_name ?? '',
    type: d.private_zone ? 'Private' : 'Public',
    records: d.record_set_count ?? 0,
    zone_id: d.zone_id ?? '',
  }[col] ?? '')), [filtered, sort])

  if (items.length === 0) {
    return <Text c="dimmed" size="sm" py="md">Nenhum recurso encontrado.</Text>
  }
  return (
    <Stack gap="xs">
    {selectedZone && (
      <ZoneRecordsDrawer zone={selectedZone} tenantId={tenantId} onClose={() => setSelectedZone(null)} />
    )}
    <FilterInput value={filter} onChange={setFilter} />
    {sorted.length === 0 ? (
      <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{filter}".</Text>
    ) : (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <SortableTh col="name" sort={sort} onToggle={toggle}>Zona / Domínio</SortableTh>
          <SortableTh col="type" sort={sort} onToggle={toggle}>Tipo</SortableTh>
          <SortableTh col="records" sort={sort} onToggle={toggle}>Records</SortableTh>
          <SortableTh col="zone_id" sort={sort} onToggle={toggle}>Zone ID</SortableTh>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sorted.map((domain, i) => (
          <Table.Tr
            key={i}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedZone(domain)}
          >
            <Table.Td fw={500}>{domain.name ?? domain.domain_name ?? '—'}</Table.Td>
            <Table.Td>
              <Badge size="sm" color={domain.private_zone ? 'gray' : 'blue'} variant="light">
                {domain.private_zone ? 'Private' : 'Public'}
              </Badge>
            </Table.Td>
            <Table.Td>{domain.record_set_count ?? '—'}</Table.Td>
            <Table.Td><Text size="xs" ff="monospace">{domain.zone_id ?? '—'}</Text></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
    )}
    </Stack>
  )
}

// ── Skeleton ───────────────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <Stack gap="xs">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} height={36} radius="sm" />
      ))}
    </Stack>
  )
}

// ── Deploy Modal ────────────────────────────────────────────────────────────────

// ── Metrics ────────────────────────────────────────────────────────────────────

interface MetricsTarget { instanceId: string; instanceName: string; tenantId: number }

interface MetricProcess {
  user: string
  pid: string
  cpu: number
  mem: number
  cmd: string
}

interface MetricReading {
  cpu_pct: number
  cores: number[]
  mem_used_gb: number
  mem_total_gb: number
  disk_used_gb: number
  disk_total_gb: number
  processes: MetricProcess[]
  ts: number
}

function metricColor(pct: number): string {
  if (pct < 60) return 'green'
  if (pct < 80) return 'orange'
  return 'red'
}

function fmtBytesPerSec(b: number): string {
  if (b < 1024) return `${b} B/s`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB/s`
  return `${(b / (1024 * 1024)).toFixed(1)} MB/s`
}

function fmtTotalBytes(b: number): string {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const VALIDATION_COLOR: Record<NetworkValidation['status'], string> = {
  OK: 'green',
  BLOCKED: 'red',
  CONTAINER_DOWN: 'orange',
  UNREACHABLE: 'red',
}

const VALIDATION_LABEL: Record<NetworkValidation['status'], string> = {
  OK: 'OK',
  BLOCKED: 'Bloqueado no SG',
  CONTAINER_DOWN: 'Container off',
  UNREACHABLE: 'Inacessível',
}

function MetricsDrawer({ target, onClose }: { target: MetricsTarget | null; onClose: () => void }) {
  const [readings, setReadings] = useState<MetricReading[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchMetrics = useCallback(async () => {
    if (!target) return
    setLoading(true)
    try {
      const res = await api.get<Omit<MetricReading, 'ts'>>(
        `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/metrics`
      )
      setReadings(prev => [...prev.slice(-23), { ...res.data, ts: Date.now() }])
      setError(null)
    } catch (e: any) {
      setError(formatApiError(e, 'Erro ao buscar métricas'))
    } finally {
      setLoading(false)
    }
  }, [target])

  useEffect(() => {
    if (!target) {
      setReadings([])
      setError(null)
      if (pollRef.current) clearInterval(pollRef.current)
      return
    }
    fetchMetrics()
    pollRef.current = setInterval(fetchMetrics, 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [target, fetchMetrics])

  const latest = readings[readings.length - 1]
  const cpuPct = latest?.cpu_pct ?? 0
  const memPct = latest ? Math.round((latest.mem_used_gb / latest.mem_total_gb) * 100) : 0
  const diskPct = latest ? Math.round((latest.disk_used_gb / latest.disk_total_gb) * 100) : 0

  const chartData = readings.map((r, i) => ({
    t: `-${(readings.length - 1 - i) * 5}s`,
    'CPU %': r.cpu_pct,
  }))

  return (
    <ResizableDrawer
      storageKey="metrics-drawer-width"
      defaultWidth={600}
      minWidth={450}
      opened={!!target}
      onClose={onClose}
      title={
        <Group gap={8}>
          <IconDeviceDesktopAnalytics size={18} />
          <Text fw={600} size="sm">Métricas — {target?.instanceName}</Text>
        </Group>
      }
      position="right"
      padding="lg"
    >
      <Stack gap="xl">
        {error && (
          <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        {!latest && !error && (
          <Center h={200}>
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Coletando métricas via SSM…</Text>
            </Stack>
          </Center>
        )}

        {latest && (
          <>
            <SimpleGrid cols={3}>
              {/* CPU */}
              <Stack align="center" gap={4}>
                <RingProgress
                  size={110}
                  thickness={11}
                  roundCaps
                  sections={[{ value: cpuPct, color: metricColor(cpuPct) }]}
                  label={
                    <Text ta="center" fw={700} size="md" c={metricColor(cpuPct)}>
                      {cpuPct}%
                    </Text>
                  }
                />
                <Text size="sm" fw={600} c="gray.7">CPU</Text>
              </Stack>

              {/* RAM */}
              <Stack align="center" gap={4}>
                <RingProgress
                  size={110}
                  thickness={11}
                  roundCaps
                  sections={[{ value: memPct, color: metricColor(memPct) }]}
                  label={
                    <Text ta="center" fw={700} size="md" c={metricColor(memPct)}>
                      {memPct}%
                    </Text>
                  }
                />
                <Text size="sm" fw={600} c="gray.7">RAM</Text>
                <Text size="xs" c="dimmed">{latest.mem_used_gb} / {latest.mem_total_gb} GB</Text>
              </Stack>

              {/* Disk */}
              <Stack align="center" gap={4}>
                <RingProgress
                  size={110}
                  thickness={11}
                  roundCaps
                  sections={[{ value: diskPct, color: metricColor(diskPct) }]}
                  label={
                    <Text ta="center" fw={700} size="md" c={metricColor(diskPct)}>
                      {diskPct}%
                    </Text>
                  }
                />
                <Text size="sm" fw={600} c="gray.7">Disco</Text>
                <Text size="xs" c="dimmed">{latest.disk_used_gb} / {latest.disk_total_gb} GB</Text>
              </Stack>
            </SimpleGrid>

            {latest.cores && latest.cores.length > 1 && (
              <Box>
                <Group justify="space-between" mb={6}>
                  <Text size="xs" fw={600} c="gray.6" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                    Núcleos ({latest.cores.length})
                  </Text>
                  <Text size="xs" c="dimmed">Média: {latest.cpu_pct}%</Text>
                </Group>
                <Stack gap={5}>
                  {latest.cores.map((pct, i) => (
                    <Group key={i} gap={8} align="center" wrap="nowrap">
                      <Text size="xs" c="dimmed" w={34} style={{ fontFamily: 'monospace' }}>
                        cpu{i}
                      </Text>
                      <Progress
                        value={pct}
                        color={metricColor(pct)}
                        size={6}
                        radius="xl"
                        style={{ flex: 1 }}
                      />
                      <Text size="xs" fw={500} w={34} ta="right" c={metricColor(pct)}>
                        {pct}%
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={600} c="gray.7">CPU % — tempo real (últimos ~2 min)</Text>
                {loading && <Loader size="xs" />}
              </Group>
              <AreaChart
                h={180}
                data={chartData}
                dataKey="t"
                series={[{ name: 'CPU %', color: metricColor(cpuPct) }]}
                curveType="natural"
                withDots={false}
                withLegend={false}
                yAxisProps={{ domain: [0, 100], tickCount: 6 }}
                xAxisProps={{ interval: 'preserveStartEnd' }}
                fillOpacity={0.15}
              />
            </Box>

            {latest.processes && latest.processes.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={8} style={{ letterSpacing: '0.05em' }}>
                    Processos — top 15 por CPU
                  </Text>
                  <ScrollArea h={260}>
                    <Table fz="xs" verticalSpacing={4} highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Processo</Table.Th>
                          <Table.Th style={{ width: 52 }}>CPU%</Table.Th>
                          <Table.Th style={{ width: 50 }}>MEM%</Table.Th>
                          <Table.Th style={{ width: 56 }}>PID</Table.Th>
                          <Table.Th style={{ width: 72 }}>Usuário</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {latest.processes.map((p, i) => (
                          <Table.Tr key={i}>
                            <Table.Td>
                              <Group gap={6} wrap="nowrap">
                                <Progress
                                  value={Math.min(p.cpu * 5, 100)}
                                  color={metricColor(p.cpu * 5)}
                                  size={4}
                                  w={28}
                                  radius="xl"
                                />
                                <Text
                                  size="xs"
                                  style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}
                                >
                                  {p.cmd}
                                </Text>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" fw={p.cpu >= 1 ? 600 : 400} c={p.cpu >= 10 ? metricColor(p.cpu) : undefined}>
                                {p.cpu.toFixed(1)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">{p.mem.toFixed(1)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{p.pid}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">{p.user}</Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </ResizableDrawer>
  )
}

// ── Health Drawer ───────────────────────────────────────────────────────────────

interface HealthTarget { instanceId: string; instanceName: string; tenantId: number }

function fmtUptime(s: number): string {
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return `${d}d ${h}h ${m}m`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function fmtKb(kb: number): string {
  if (kb === 0) return '0 B'
  if (kb < 1024) return `${kb} KB`
  if (kb < 1024 * 1024) return `${(kb / 1024).toFixed(1)} MB`
  return `${(kb / (1024 * 1024)).toFixed(2)} GB`
}

function HealthDrawer({ target, onClose }: { target: HealthTarget | null; onClose: () => void }) {
  const [data, setData] = useState<HealthInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { width: drawerWidth, onResizeStart } = useResizableDrawer({
    storageKey: 'health-drawer-width',
    defaultWidth: 700,
    minWidth: 500,
  })

  const fetchHealth = useCallback(async () => {
    if (!target) return
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<HealthInfo>(
        `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/health`
      )
      setData(res.data)
    } catch (e: any) {
      setError(formatApiError(e, 'Erro ao buscar informações de health'))
    } finally {
      setLoading(false)
    }
  }, [target])

  useEffect(() => {
    if (!target) { setData(null); setError(null); return }
    fetchHealth()
  }, [target, fetchHealth])

  const hasRestarts = (data?.containers ?? []).some(c => c.restarts > 0)
  const hasDiskWarning = (data?.disks ?? []).some(d => d.pct >= 80)

  return (
    <>
      {!!target && (
        <Box
          className="drawer-resize-handle"
          onMouseDown={onResizeStart}
          style={{
            position: 'fixed', top: 0, right: drawerWidth, width: 6,
            height: '100%', cursor: 'ew-resize', zIndex: 300,
          }}
        />
      )}
    <Drawer
      opened={!!target}
      onClose={onClose}
      title={
        <Group gap={8}>
          <IconActivity size={18} color="green" />
          <Text fw={600} size="sm">Health — {target?.instanceName}</Text>
          {(hasRestarts || hasDiskWarning) && !loading && (
            <Badge size="xs" color="red" variant="filled">Atenção</Badge>
          )}
        </Group>
      }
      position="right"
      size={drawerWidth}
      scrollAreaComponent={ScrollArea.Autosize}
      padding="lg"
    >
      <Stack gap="xl">
        <Group justify="flex-end">
          <Button size="xs" variant="subtle" leftSection={<IconRefresh size={13} />} loading={loading} onClick={fetchHealth}>
            Atualizar
          </Button>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light">{error}</Alert>
        )}

        {loading && !data && (
          <Center h={200}>
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Coletando health info via SSM…</Text>
            </Stack>
          </Center>
        )}

        {data && (
          <>
            {/* ── Sistema ── */}
            <Box>
              <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                Sistema
              </Text>
              <SimpleGrid cols={4}>
                <Box>
                  <Text size="xs" c="dimmed" mb={2}>Uptime</Text>
                  <Text size="sm" fw={600}>{fmtUptime(data.uptime_s)}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" mb={2}>Load (1m)</Text>
                  <Text size="sm" fw={600} c={data.load_avg[0] > 2 ? 'red' : data.load_avg[0] > 1 ? 'orange' : undefined}>
                    {data.load_avg[0].toFixed(2)}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" mb={2}>Load (5m / 15m)</Text>
                  <Text size="sm" fw={500} c="dimmed">{data.load_avg[1].toFixed(2)} / {data.load_avg[2].toFixed(2)}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" mb={2}>Swap usado</Text>
                  <Text size="sm" fw={600} c={data.swap_total_kb > 0 && (data.swap_used_kb / data.swap_total_kb) > 0.5 ? 'orange' : undefined}>
                    {fmtKb(data.swap_used_kb)} / {fmtKb(data.swap_total_kb)}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* ── Disco ── */}
            <Box>
              <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                Disco
              </Text>
              <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={6}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ponto de montagem</Table.Th>
                    <Table.Th>Filesystem</Table.Th>
                    <Table.Th style={{ width: 200 }}>Uso</Table.Th>
                    <Table.Th style={{ width: 80 }}>Livre</Table.Th>
                    <Table.Th style={{ width: 60 }}>%</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.disks.map((d, i) => {
                    const color = d.pct >= 90 ? 'red' : d.pct >= 80 ? 'orange' : 'green'
                    return (
                      <Table.Tr key={i}>
                        <Table.Td><Text size="xs" ff="monospace" fw={600}>{d.mount}</Text></Table.Td>
                        <Table.Td><Text size="xs" ff="monospace" c="dimmed">{d.filesystem}</Text></Table.Td>
                        <Table.Td>
                          <Stack gap={3}>
                            <Progress value={d.pct} color={color} size={6} radius="xl" />
                            <Text size="xs" c="dimmed">{fmtKb(d.used_kb)} / {fmtKb(d.total_kb)}</Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td><Text size="xs" ff="monospace">{fmtKb(d.avail_kb)}</Text></Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={600} c={color}>{d.pct}%</Text>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Box>

            <Divider />

            {/* ── Containers ── */}
            <Box>
              <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                Containers Docker
              </Text>
              {data.containers.length === 0 ? (
                <Text size="xs" c="dimmed">Nenhum container em execução.</Text>
              ) : (
                <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={6}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Container</Table.Th>
                      <Table.Th style={{ width: 80 }}>CPU %</Table.Th>
                      <Table.Th style={{ width: 180 }}>Memória</Table.Th>
                      <Table.Th style={{ width: 90 }}>Restarts</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.containers.map((c, i) => {
                      const cpuVal = parseFloat(c.cpu_pct) || 0
                      const memPctVal = parseFloat(c.mem_pct) || 0
                      return (
                        <Table.Tr key={i}>
                          <Table.Td><Text size="xs" ff="monospace" fw={500}>{c.name}</Text></Table.Td>
                          <Table.Td>
                            <Text size="xs" fw={600} c={metricColor(cpuVal)}>{c.cpu_pct}%</Text>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={3}>
                              <Progress value={memPctVal} color={metricColor(memPctVal)} size={5} radius="xl" />
                              <Text size="xs" c="dimmed">{c.mem_used_mb} / {c.mem_total_mb} MB</Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            {c.restarts > 0 ? (
                              <Badge size="xs" color="red" variant="filled">{c.restarts}</Badge>
                            ) : (
                              <Badge size="xs" color="green" variant="light">0</Badge>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Box>

            {/* ── EBS Volumes ── */}
            {data.volumes.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                    Volumes EBS ({data.volumes.length})
                  </Text>
                  <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={6}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Volume ID</Table.Th>
                        <Table.Th>Device</Table.Th>
                        <Table.Th style={{ width: 60 }}>Tipo</Table.Th>
                        <Table.Th style={{ width: 70 }}>Tamanho</Table.Th>
                        <Table.Th style={{ width: 70 }}>IOPS</Table.Th>
                        <Table.Th style={{ width: 80 }}>Estado</Table.Th>
                        <Table.Th style={{ width: 70 }}>Encript.</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.volumes.map((v, i) => (
                        <Table.Tr key={i}>
                          <Table.Td><Text size="xs" ff="monospace" c="dimmed">{v.volume_id}</Text></Table.Td>
                          <Table.Td><Text size="xs" ff="monospace">{v.device}</Text></Table.Td>
                          <Table.Td><Badge size="xs" variant="outline" color="gray">{v.type}</Badge></Table.Td>
                          <Table.Td><Text size="xs">{v.size_gb} GB</Text></Table.Td>
                          <Table.Td><Text size="xs" c="dimmed">{v.iops ?? '—'}</Text></Table.Td>
                          <Table.Td>
                            <Badge size="xs" color={v.attach_state === 'attached' ? 'green' : 'orange'} variant="light">
                              {v.attach_state}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="xs" color={v.encrypted ? 'green' : 'gray'} variant="light">
                              {v.encrypted ? 'sim' : 'não'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </Drawer>
    </>
  )
}

// ── Network Drawer ──────────────────────────────────────────────────────────────

interface NetworkTarget { instanceId: string; instanceName: string; tenantId: number }

function NetworkDrawer({ target, onClose }: { target: NetworkTarget | null; onClose: () => void }) {
  const [data, setData] = useState<NetworkInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchNetwork = useCallback(async () => {
    if (!target) return
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<NetworkInfo>(
        `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/network`
      )
      setData(res.data)
    } catch (e: any) {
      setError(formatApiError(e, 'Erro ao buscar informações de rede'))
    } finally {
      setLoading(false)
    }
  }, [target])

  useEffect(() => {
    if (!target) { setData(null); setError(null); return }
    fetchNetwork()
  }, [target, fetchNetwork])

  const validation = data?.validation ?? []
  const interfaces = data?.traffic?.interfaces ?? []
  const osPorts = data?.os_ports ?? []

  const hasIssues = validation.some(v => v.severity !== 'ok')

  return (
    <ResizableDrawer
      storageKey="network-drawer-width"
      defaultWidth={700}
      minWidth={500}
      opened={!!target}
      onClose={onClose}
      title={
        <Group gap={8}>
          <IconNetwork size={18} />
          <Text fw={600} size="sm">Rede — {target?.instanceName}</Text>
          {hasIssues && !loading && (
            <Badge size="xs" color="red" variant="filled">Problemas detectados</Badge>
          )}
        </Group>
      }
      position="right"
      padding="lg"
    >
      <Stack gap="xl">
        <Group justify="flex-end">
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconRefresh size={13} />}
            loading={loading}
            onClick={fetchNetwork}
          >
            Atualizar
          </Button>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        {loading && !data && (
          <Center h={200}>
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Coletando dados de rede via SSM…</Text>
            </Stack>
          </Center>
        )}

        {data && (
          <>
            {/* ── Validação de Portas ── */}
            <Box>
              <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                Validação de Portas — Docker × OS × Security Group
              </Text>
              {validation.length === 0 ? (
                <Alert color="blue" variant="light" icon={<IconAlertCircle size={14} />}>
                  <Text size="xs">Nenhum container com bind 0.0.0.0 detectado.</Text>
                </Alert>
              ) : (
                <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={6}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Container</Table.Th>
                      <Table.Th style={{ width: 70 }}>Porta</Table.Th>
                      <Table.Th style={{ width: 60 }}>Proto</Table.Th>
                      <Table.Th style={{ width: 80 }}>OS ouve</Table.Th>
                      <Table.Th style={{ width: 80 }}>SG aberto</Table.Th>
                      <Table.Th style={{ width: 140 }}>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {validation.map((v, i) => (
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Text size="xs" ff="monospace">{v.container}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="monospace" fw={600}>{v.port}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" variant="outline" color="gray">{v.protocol}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" color={v.os_listening ? 'green' : 'red'} variant="light">
                            {v.os_listening ? 'sim' : 'não'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" color={v.sg_open ? 'green' : 'red'} variant="light">
                            {v.sg_open ? 'sim' : 'não'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="xs" color={VALIDATION_COLOR[v.status]} variant={v.status === 'OK' ? 'light' : 'filled'}>
                            {VALIDATION_LABEL[v.status]}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Box>

            <Divider />

            {/* ── Tráfego de Rede ── */}
            <Box>
              <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                Tráfego de Rede — snapshot 1s
              </Text>
              {interfaces.length === 0 ? (
                <Text size="xs" c="dimmed">Nenhuma interface detectada.</Text>
              ) : (
                <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={6}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Interface</Table.Th>
                      <Table.Th style={{ width: 110 }}>RX (agora)</Table.Th>
                      <Table.Th style={{ width: 110 }}>TX (agora)</Table.Th>
                      <Table.Th style={{ width: 110 }}>RX total</Table.Th>
                      <Table.Th style={{ width: 110 }}>TX total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {interfaces.map((iface, i) => (
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Text size="xs" ff="monospace" fw={600}>{iface.name}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <IconArrowDown size={11} color="teal" />
                            <Text size="xs" ff="monospace" c="teal">{fmtBytesPerSec(iface.rx_bytes_sec)}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <IconArrowUp size={11} color="blue" />
                            <Text size="xs" ff="monospace" c="blue">{fmtBytesPerSec(iface.tx_bytes_sec)}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="monospace" c="dimmed">{fmtTotalBytes(iface.rx_total_bytes)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="monospace" c="dimmed">{fmtTotalBytes(iface.tx_total_bytes)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Box>

            {/* ── Portas ouvindo no OS ── */}
            {osPorts.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text size="xs" fw={600} c="gray.6" tt="uppercase" mb={10} style={{ letterSpacing: '0.05em' }}>
                    Portas em escuta no OS ({osPorts.length})
                  </Text>
                  <ScrollArea h={220}>
                    <Table fz="xs" verticalSpacing={4} highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th style={{ width: 70 }}>Porta</Table.Th>
                          <Table.Th style={{ width: 60 }}>Proto</Table.Th>
                          <Table.Th>Processo</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {osPorts.map((p, i) => (
                          <Table.Tr key={i}>
                            <Table.Td>
                              <Text size="xs" ff="monospace" fw={600}>{p.port}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="xs" variant="outline" color="gray">{p.protocol}</Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" ff="monospace" c="dimmed">{p.process || '—'}</Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </ResizableDrawer>
  )
}

interface DeployTarget { instanceId: string; instanceName: string; tenantId: number }

// Summary rows for the deploy success screen — mirrors HardRestart's
// SummaryRow shape but inline since the deploy data shape is small.
function DeploySummaryRows({
  target,
  startedAt,
  duration,
  commandId,
}: {
  target: DeployTarget | null
  startedAt: number | null
  duration: string | null
  commandId: string | null
}) {
  if (!target) return null
  return (
    <Box
      p={12}
      style={{
        background: SURFACE.card,
        border: `1px solid ${SURFACE.cardBorder}`,
        borderRadius: 8,
      }}
    >
      <Stack gap={8}>
        <DeployRow label="Instância" value={target.instanceName} />
        <DeployRow label="Instance ID" value={target.instanceId} mono />
        {startedAt && (
          <DeployRow label="Início" value={new Date(startedAt).toLocaleString('pt-BR')} />
        )}
        {duration && <DeployRow label="Duração" value={duration} mono />}
        {commandId && <DeployRow label="Command ID" value={commandId.slice(0, 18) + '…'} mono />}
      </Stack>
    </Box>
  )
}

function DeployRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Group justify="space-between" align="baseline" wrap="nowrap">
      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text
        size="xs"
        style={{
          fontFamily: mono ? '"JetBrains Mono", monospace' : '"Barlow", sans-serif',
          color: SURFACE.textPrimary,
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={value}
      >
        {value}
      </Text>
    </Group>
  )
}

// Collapsible terminal-style block showing the full deploy.sh output
// (only meaningful after completion; SSM doesn't stream during execution).
const DeployOutputViewer = forwardRef<HTMLPreElement, { output: string; defaultOpen?: boolean }>(
  function DeployOutputViewer({ output, defaultOpen }, ref) {
    const [open, setOpen] = useState(!!defaultOpen)
    if (!output) return null
    return (
      <Box>
        <UnstyledButton
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-label={open ? 'Recolher saída' : 'Expandir saída'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 0' }}
        >
          <motion.span
            animate={{ rotate: open ? 90 : 0 }}
            transition={{ duration: 0.18 }}
            style={{ display: 'inline-flex', color: SURFACE.textSecondary }}
          >
            <IconChevronRight size={14} />
          </motion.span>
          <Text size="xs" c="dimmed">
            {open ? 'Ocultar saída completa' : `Ver saída completa (${output.length.toLocaleString('pt-BR')} chars)`}
          </Text>
        </UnstyledButton>
        <Collapse in={open}>
          <Box
            component="pre"
            ref={ref}
            mt={6}
            p={12}
            style={{
              background: COLOR.surfaceDark,
              color: '#c9d1d9',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12,
              borderRadius: 6,
              minHeight: 120,
              maxHeight: 420,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {output}
          </Box>
        </Collapse>
      </Box>
    )
  },
)

function DeployModal({ target, onClose }: { target: DeployTarget | null; onClose: () => void }) {
  const [reason, setReason] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [commandId, setCommandId] = useState<string | null>(null)
  const [output, setOutput] = useState('')
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle')
  const [deployStatus, setDeployStatus] = useState('')
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [commandIdReceivedAt, setCommandIdReceivedAt] = useState<number | null>(null)
  const [inProgressAt, setInProgressAt] = useState<number | null>(null)
  const [endedAt, setEndedAt] = useState<number | null>(null)
  const logRef = useRef<HTMLPreElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const qc = useQueryClient()
  // Re-render every 1s while running so the elapsed-time badge ticks even
  // before the next polling response.
  useTicker(status === 'running' ? 1000 : 60_000)

  const reset = () => {
    setReason(''); setConfirmation(''); setCommandId(null)
    setOutput(''); setStatus('idle'); setDeployStatus(''); setStartedAt(null)
    setCommandIdReceivedAt(null); setInProgressAt(null); setEndedAt(null)
    if (pollRef.current) clearInterval(pollRef.current)
  }

  const handleClose = () => { reset(); onClose() }

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [output])

  useEffect(() => {
    if (!commandId || !target) return
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get<{ status: string; output: string; done: boolean }>(
          `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/deploy/${commandId}`
        )
        setOutput(res.data.output)
        setDeployStatus(res.data.status)
        // Mark the InProgress transition once — drives the live timeline
        if (res.data.status === 'InProgress') {
          setInProgressAt(prev => prev ?? Date.now())
        }
        if (res.data.done) {
          clearInterval(pollRef.current!)
          setEndedAt(Date.now())
          setStatus(res.data.status === 'Success' ? 'done' : 'error')
          qc.invalidateQueries({ queryKey: ['inventory-ec2'] })
        }
      } catch {
        clearInterval(pollRef.current!)
        setEndedAt(Date.now())
        setStatus('error')
      }
    }, 2000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [commandId, target])

  const handleDeploy = async () => {
    if (!target) return
    setStatus('running'); setOutput(''); setStartedAt(Date.now())
    setCommandIdReceivedAt(null); setInProgressAt(null); setEndedAt(null)
    try {
      const res = await api.post<{ command_id: string }>(
        `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/deploy`,
        { reason, confirmation: true }
      )
      setCommandId(res.data.command_id)
      setCommandIdReceivedAt(Date.now())
    } catch (e: any) {
      setOutput(formatApiError(e, String(e)))
      setEndedAt(Date.now())
      setStatus('error')
    }
  }

  const elapsedLabel =
    startedAt && status === 'running'
      ? formatElapsed(Date.now() - startedAt)
      : null

  const canStart = reason.trim().length > 0 && confirmation === 'DEPLOY'
  const statusColor = status === 'done' ? 'green' : status === 'error' ? 'red' : 'blue'
  const statusLabel = { idle: '', running: 'Executando...', done: 'Concluído', error: 'Falhou' }[status]

  // Cardiograph state mapping
  const cardiographState: 'idle' | 'inspecting' | 'healthy' | 'failed' =
    status === 'done' ? 'healthy'
    : status === 'error' ? 'failed'
    : status === 'running' ? 'inspecting'
    : 'idle'

  const totalDuration =
    startedAt && endedAt ? formatElapsed(endedAt - startedAt) : null

  // Parse deploy.sh output retroactively into phases for the result view.
  // Live view can't show phases (SSM doesn't stream); this gives the user
  // the same "etapas" visualization as hard-restart, after completion.
  const parsedDeploy = useMemo(
    () => (output && (status === 'done' || status === 'error') ? parseDeployOutput(output) : null),
    [output, status],
  )

  return (
    <Modal
      opened={!!target}
      onClose={handleClose}
      title={
        <span style={{ fontFamily: '"Barlow", sans-serif', fontWeight: 700 }}>
          Deploy
        </span>
      }
      size={status === 'idle' ? 'md' : 'xl'}
      closeOnClickOutside={status !== 'running'}
      closeOnEscape={status !== 'running'}
      overlayProps={{ blur: 2 }}
    >
      <AnimatePresence mode="wait">
        {/* ── PREFLIGHT ─────────────────────────────────────────────── */}
        {status === 'idle' && (
          <motion.div
            key="deploy-preflight"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={MOTION.viewSwap}
          >
            <Stack gap="md">
              <InstanceCard
                instanceName={target?.instanceName ?? ''}
                instanceId={target?.instanceId ?? ''}
              />
              <Text size="sm" c="dimmed">
                Executa <Code>/home/ubuntu/devops/deploy.sh</Code> na instância via SSM.
              </Text>
              <Textarea
                label="Motivo do deploy"
                placeholder="Ex: release v2.3.1, hotfix autenticação..."
                required
                minRows={2}
                autosize
                value={reason}
                onChange={e => setReason(e.currentTarget.value)}
              />
              <TextInput
                label='Digite "DEPLOY" para confirmar'
                placeholder="DEPLOY"
                value={confirmation}
                onChange={e => setConfirmation(e.currentTarget.value.toUpperCase())}
              />
              <Group justify="flex-end" mt="sm">
                <Button variant="subtle" color="gray" onClick={handleClose}>Cancelar</Button>
                <Button color="orange" disabled={!canStart} onClick={handleDeploy}>
                  Iniciar Deploy
                </Button>
              </Group>
            </Stack>
          </motion.div>
        )}

        {/* ── LIVE ──────────────────────────────────────────────────── */}
        {status === 'running' && (
          <motion.div
            key="deploy-live"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={MOTION.viewSwap}
          >
            <Stack gap="md">
              <InstanceCard
                instanceName={target?.instanceName ?? ''}
                instanceId={target?.instanceId ?? ''}
                operationStartedAt={startedAt ?? undefined}
              />
              <Box
                p={12}
                style={{
                  background: SURFACE.card,
                  border: `1px solid ${SURFACE.cardBorder}`,
                  borderRadius: 8,
                }}
              >
                <HeroCardiograph
                  state={cardiographState}
                  meta={
                    deployStatus
                      ? `deploy.sh • ${deployStatus.toLowerCase()}`
                      : 'deploy.sh em execução'
                  }
                />
              </Box>
              <Box
                role="status"
                aria-live="polite"
              >
                <DeployLifecycleTimeline
                  commandIdReceived={!!commandId}
                  deployStatus={deployStatus}
                  status={status}
                  startedAt={startedAt}
                  commandIdReceivedAt={commandIdReceivedAt}
                  inProgressAt={inProgressAt}
                  endedAt={endedAt}
                />
              </Box>
            </Stack>
          </motion.div>
        )}

        {/* ── SUCCESS ───────────────────────────────────────────────── */}
        {status === 'done' && (
          <motion.div
            key="deploy-result"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={MOTION.viewSwap}
          >
            <Stack gap="md">
              <Box
                p={16}
                style={{
                  background: COLOR.brandGradient,
                  color: '#fff',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <Stack gap={4}>
                  <Text fw={700} size="lg" style={{ fontFamily: '"Barlow", sans-serif' }}>
                    Deploy concluído
                  </Text>
                  <Text size="xs" style={{ opacity: 0.85 }}>
                    {target?.instanceName ?? target?.instanceId}
                    {totalDuration ? ` · ${totalDuration}` : ''}
                  </Text>
                </Stack>
                <Box mt={12} style={{ opacity: 0.92 }}>
                  <HeroCardiograph state="healthy" height={64} />
                </Box>
              </Box>
              <DeploySummaryRows
                target={target}
                startedAt={startedAt}
                duration={totalDuration}
                commandId={commandId}
              />
              <Box
                p={12}
                style={{
                  background: SURFACE.card,
                  border: `1px solid ${SURFACE.cardBorder}`,
                  borderRadius: 8,
                }}
              >
                <DeployLifecycleTimeline
                  commandIdReceived={!!commandId}
                  deployStatus={deployStatus}
                  status={status}
                  startedAt={startedAt}
                  commandIdReceivedAt={commandIdReceivedAt}
                  inProgressAt={inProgressAt}
                  endedAt={endedAt}
                />
              </Box>
              {parsedDeploy && parsedDeploy.phases.length > 0 && (
                <Box
                  p={12}
                  style={{
                    background: SURFACE.card,
                    border: `1px solid ${SURFACE.cardBorder}`,
                    borderRadius: 8,
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <Text size="xs" fw={600} c="dimmed" mb={8} pl={4}>
                    Detalhes do docker compose
                  </Text>
                  <DeployTimeline phases={parsedDeploy.phases} />
                </Box>
              )}
              <DeployOutputViewer ref={logRef} output={output} defaultOpen={false} />
              <Group justify="flex-end">
                <Button variant="default" onClick={handleClose}>Fechar</Button>
              </Group>
            </Stack>
          </motion.div>
        )}

        {/* ── ERROR ─────────────────────────────────────────────────── */}
        {status === 'error' && (
          <motion.div
            key="deploy-error"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={MOTION.viewSwap}
          >
            <Stack gap="md">
              <Box
                p={16}
                style={{
                  background: COLOR.danger,
                  color: '#fff',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <Stack gap={4}>
                  <Text fw={700} size="lg" style={{ fontFamily: '"Barlow", sans-serif' }}>
                    Deploy falhou
                  </Text>
                  <Text size="xs" style={{ opacity: 0.85 }}>
                    {target?.instanceName ?? target?.instanceId}
                    {totalDuration ? ` · ${totalDuration}` : ''}
                  </Text>
                </Stack>
                <Box mt={12}>
                  <HeroCardiograph state="failed" height={56} />
                </Box>
              </Box>
              <Box
                p={12}
                style={{
                  background: SURFACE.card,
                  border: `1px solid ${SURFACE.cardBorder}`,
                  borderRadius: 8,
                }}
              >
                <DeployLifecycleTimeline
                  commandIdReceived={!!commandId}
                  deployStatus={deployStatus}
                  status={status}
                  startedAt={startedAt}
                  commandIdReceivedAt={commandIdReceivedAt}
                  inProgressAt={inProgressAt}
                  endedAt={endedAt}
                />
              </Box>
              {parsedDeploy && parsedDeploy.phases.length > 0 && (
                <Box
                  p={12}
                  style={{
                    background: SURFACE.card,
                    border: `1px solid ${SURFACE.cardBorder}`,
                    borderRadius: 8,
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <Text size="xs" fw={600} c="dimmed" mb={8} pl={4}>
                    Detalhes do docker compose
                  </Text>
                  <DeployTimeline phases={parsedDeploy.phases} />
                </Box>
              )}
              <DeployOutputViewer ref={logRef} output={output} defaultOpen />
              <Group justify="flex-end">
                <Button variant="default" onClick={handleClose}>Fechar</Button>
              </Group>
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  )
}

// ── Docker Drawer ───────────────────────────────────────────────────────────────

function dockerStateColor(state: string) {
  if (state === 'running') return 'green'
  if (state === 'paused') return 'yellow'
  return 'red'
}

// ── Docker container action modal ──────────────────────────────────────────────

type DockerActionMode = 'restart' | 'hard-restart'

interface DockerActionTarget {
  mode: DockerActionMode
  containerName: string
  instanceId: string
  tenantId: number
}

function DockerActionModal({ target, onClose }: { target: DockerActionTarget | null; onClose: () => void }) {
  const [reason, setReason] = useState('')
  const [cleanVolumes, setCleanVolumes] = useState(false)
  const qc = useQueryClient()

  useEffect(() => {
    if (!target) { setReason(''); setCleanVolumes(false) }
  }, [target])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) return
      const url = `/v1/tenants/${target.tenantId}/inventory/ec2/${target.instanceId}/docker/${encodeURIComponent(target.containerName)}/${target.mode}`
      await api.post(url, { reason, confirmation: true, clean_volumes: cleanVolumes })
    },
    onSuccess: () => {
      notifications.show({
        color: target?.mode === 'restart' ? 'green' : 'orange',
        title: target?.mode === 'restart' ? 'Restart solicitado' : 'Hard restart iniciado',
        message: `Container ${target?.containerName.replace(/^\//, '')} será reiniciado em instantes.`,
      })
      qc.invalidateQueries({ queryKey: ['docker-containers'] })
      onClose()
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Erro', message: err.message })
    },
  })

  if (!target) return null

  const isHard = target.mode === 'hard-restart'
  const containerLabel = target.containerName.replace(/^\//, '')

  return (
    <Modal
      opened={!!target}
      onClose={onClose}
      title={
        <Group gap={8}>
          {isHard ? <IconRotate size={18} color="orange" /> : <IconPlayerPlay size={18} color="green" />}
          <Text fw={600}>{isHard ? 'Hard Restart' : 'Restart'} — {containerLabel}</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {isHard && (
          <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
            O container será <strong>parado, removido</strong>, a imagem será atualizada do registry e o container recriado com a mesma configuração.
          </Alert>
        )}

        <Textarea
          label="Motivo"
          placeholder="Descreva o motivo do restart…"
          value={reason}
          onChange={e => setReason(e.currentTarget.value)}
          minRows={2}
          required
        />

        {isHard && (
          <>
            <Divider />
            <Checkbox
              label="Limpar volumes nomeados"
              description="Remove os volumes associados a este container. DADOS SERÃO PERDIDOS de forma irreversível."
              checked={cleanVolumes}
              onChange={e => setCleanVolumes(e.currentTarget.checked)}
              color="red"
            />
            {cleanVolumes && (
              <Alert color="red" variant="filled" icon={<IconAlertCircle size={16} />}>
                ⚠ ATENÇÃO: os volumes deste container serão apagados permanentemente. Esta ação não pode ser desfeita.
              </Alert>
            )}
          </>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={mutation.isPending}>Cancelar</Button>
          <Button
            color={isHard ? (cleanVolumes ? 'red' : 'orange') : 'green'}
            loading={mutation.isPending}
            disabled={!reason.trim()}
            onClick={() => mutation.mutate()}
          >
            {isHard ? (cleanVolumes ? 'Hard Restart + Limpar Volumes' : 'Hard Restart') : 'Restart'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

interface DockerDrawerProps {
  instance: EC2Instance | null
  tenantId: number | undefined
  onClose: () => void
  onPin: (win: Omit<PinnedWindow, 'id'>) => void
}

type DockerCol = 'name' | 'image' | 'state' | 'ports' | 'running_for'

function DockerDrawer({ instance, tenantId, onClose, onPin }: DockerDrawerProps) {
  const [logsTarget, setLogsTarget] = useState<string | null>(null)
  const [dockerAction, setDockerAction] = useState<DockerActionTarget | null>(null)
  const [hardRestartTarget, setHardRestartTarget] = useState<HardRestartTarget | null>(null)
  const { sort: dockerSort, toggle: dockerToggle } = useSortable<DockerCol>()
  const { width: drawerWidth, onResizeStart } = useResizableDrawer({
    storageKey: 'docker-drawer-width',
    defaultWidth: 700,
    minWidth: 400,
  })
  const { data, isLoading, error, refetch, isFetching } = useQuery<{ containers: DockerContainer[] }, Error>({
    queryKey: ['docker-containers', tenantId, instance?.instance_id],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/inventory/ec2/${instance!.instance_id}/docker`)
      return res.data
    },
    enabled: !!instance && !!tenantId,
    retry: false,
    staleTime: 0,
  })

  const containers = data?.containers ?? []
  const [dockerFilter, setDockerFilter] = useState('')
  const filteredContainers = useMemo(() => filterItems(containers, dockerFilter, c => [
    c.name.replace(/^\//, ''), c.image, c.state, c.ports ?? '', c.running_for ?? '',
  ]), [containers, dockerFilter])
  const sortedContainers = useMemo(() => sortItems(filteredContainers, dockerSort, (col, c) => ({
    name: c.name.replace(/^\//, ''),
    image: c.image,
    state: c.state,
    ports: c.ports ?? '',
    running_for: c.running_for ?? '',
  }[col] ?? '')), [filteredContainers, dockerSort])

  return (
    <>
      {/* resize handle — fora do Drawer, fixo na borda esquerda do painel */}
      {!!instance && (
        <Box
          onMouseDown={onResizeStart}
          className="drawer-resize-handle"
          style={{
            position: 'fixed',
            top: 0,
            right: drawerWidth,
            width: 6,
            height: '100%',
            cursor: 'ew-resize',
            zIndex: 300,
          }}
        />
      )}
      <Drawer
        opened={!!instance}
        onClose={onClose}
        title={
          <Group gap={8}>
            <IconBrandDocker size={18} color="#1D72B8" />
            <Text fw={600} size="sm">Containers — {instance?.name ?? instance?.instance_id}</Text>
          </Group>
        }
        position="right"
        size={drawerWidth}
        scrollAreaComponent={ScrollArea.Autosize}
      >
      <Group justify="space-between" mb="md">
        <Text size="xs" c="dimmed" ff="monospace">{instance?.name ?? instance?.instance_id}</Text>
        <ActionIcon variant="subtle" size="sm" onClick={() => refetch()} loading={isFetching}>
          <IconRefresh size={14} />
        </ActionIcon>
      </Group>

      {isLoading && (
        <Stack gap={8}>
          {[1,2,3].map(i => <Skeleton key={i} height={48} radius="sm" />)}
          <Text size="xs" c="dimmed" ta="center">Executando docker ps via SSM…</Text>
        </Stack>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Erro ao buscar containers">
          {(error as Error).message}
        </Alert>
      )}

      {!isLoading && !error && containers.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">Nenhum container em execução.</Text>
      )}

      {!isLoading && containers.length > 0 && (
        <Stack gap="xs">
        <FilterInput value={dockerFilter} onChange={setDockerFilter} />
        {sortedContainers.length === 0 ? (
          <Text c="dimmed" size="sm" py="sm">Nenhum resultado para "{dockerFilter}".</Text>
        ) : (
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <SortableTh col="name" sort={dockerSort} onToggle={dockerToggle}>Nome</SortableTh>
              <SortableTh col="image" sort={dockerSort} onToggle={dockerToggle}>Imagem</SortableTh>
              <SortableTh col="state" sort={dockerSort} onToggle={dockerToggle}>Status</SortableTh>
              <SortableTh col="ports" sort={dockerSort} onToggle={dockerToggle}>Portas</SortableTh>
              <SortableTh col="running_for" sort={dockerSort} onToggle={dockerToggle}>Uptime</SortableTh>
              <Table.Th style={{ width: 90 }}>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedContainers.map(c => (
              <Table.Tr key={c.id}>
                <Table.Td>
                  <Text
                    component="span"
                    size="xs"
                    fw={500}
                    style={{ cursor: 'pointer', color: '#228be6', textDecoration: 'underline' }}
                    onClick={() => setLogsTarget(c.name)}
                  >
                    {c.name.replace(/^\//, '')}
                  </Text>
                </Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{c.image}</Text></Table.Td>
                <Table.Td>
                  <Badge size="sm" color={dockerStateColor(c.state)} variant="light">{c.state}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace">{c.ports || '—'}</Text>
                </Table.Td>
                <Table.Td><Text size="xs">{c.running_for}</Text></Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Restart" position="top">
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="green"
                        onClick={() => setDockerAction({
                          mode: 'restart',
                          containerName: c.name,
                          instanceId: instance!.instance_id,
                          tenantId: tenantId!,
                        })}
                      >
                        <IconPlayerPlay size={13} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Hard Restart" position="top">
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="orange"
                        onClick={() => setHardRestartTarget({
                          tenantId: tenantId!,
                          instanceId: instance!.instance_id,
                          containerName: c.name,
                          image: c.image,
                          uptime: c.running_for,
                        })}
                      >
                        <IconRotate size={13} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        )}
        </Stack>
      )}

      <LogsDrawer
        containerName={logsTarget}
        instanceId={instance?.instance_id ?? ''}
        instanceName={instance?.name ?? instance?.instance_id ?? ''}
        tenantId={tenantId ?? null}
        onClose={() => setLogsTarget(null)}
        onPin={onPin}
      />

      <DockerActionModal target={dockerAction} onClose={() => setDockerAction(null)} />
      <HardRestartModal
        target={hardRestartTarget}
        onClose={() => setHardRestartTarget(null)}
      />
    </Drawer>
    </>
  )
}

// ── Pinned floating windows ─────────────────────────────────────────────────────

interface PinnedWindow {
  id: string
  containerName: string
  instanceId: string
  instanceName: string
  tenantId: number
  position: { x: number; y: number }
  size: { w: number; h: number }
}

function FloatingLogWindow({ win, onClose }: { win: PinnedWindow; onClose: (id: string) => void }) {
  const [lines, setLines] = useState<string[]>([])
  const [fetching, setFetching] = useState(false)
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(paused)
  // Linha nova rola o log para o fim sem puxar quem rolou para cima
  // (consoleai#25060); pausado, a coleta para e nada chega.
  const seguirOFim = useSeguirOFim<HTMLDivElement>(lines)

  useEffect(() => { pausedRef.current = paused }, [paused])

  useEffect(() => {
    let cancelled = false
    setLines([])
    setPaused(false)

    const fetchInitial = async () => {
      setFetching(true)
      try {
        const res = await api.get(
          `/v1/tenants/${win.tenantId}/inventory/ec2/${win.instanceId}/docker/${encodeURIComponent(win.containerName)}/logs`,
          { params: { tail: 200 } },
        )
        if (!cancelled) setLines(res.data.lines ?? [])
      } catch { /* silent */ } finally {
        if (!cancelled) setFetching(false)
      }
    }
    fetchInitial()

    const timer = setInterval(async () => {
      if (pausedRef.current || cancelled) return
      try {
        const res = await api.get(
          `/v1/tenants/${win.tenantId}/inventory/ec2/${win.instanceId}/docker/${encodeURIComponent(win.containerName)}/logs`,
          { params: { since: '4s' } },
        )
        if (!cancelled) {
          const newLines: string[] = res.data.lines ?? []
          if (newLines.length > 0) setLines(prev => [...prev, ...newLines])
        }
      } catch { /* silent */ }
    }, 3000)

    return () => { cancelled = true; clearInterval(timer) }
  }, [win.containerName, win.instanceId, win.tenantId])

  const label = `${win.containerName.replace(/^\//, '')} · ${win.instanceName}`

  return (
    <JanelaFlutuante
      titulo={
        <Group gap={6} wrap="nowrap">
          <IconBrandDocker size={14} color="#1D72B8" />
          <Text size="xs" fw={600} c="#c9d1d9" truncate>
            {label}
          </Text>
          {fetching && <Loader size={10} color="blue" />}
        </Group>
      }
      acoes={
        <>
          <ActionIcon
            size="xs"
            variant="subtle"
            color={paused ? 'green' : 'orange'}
            onClick={() => setPaused(p => !p)}
            title={paused ? 'Retomar' : 'Pausar'}
          >
            <Text size="xs">{paused ? '▶' : '⏸'}</Text>
          </ActionIcon>
          <ActionIcon size="xs" variant="subtle" color="gray"
            onClick={() => setLines([])} title="Limpar">
            <Text size="xs">✕</Text>
          </ActionIcon>
        </>
      }
      inicial={{ ...win.position, ...win.size }}
      onFechar={() => onClose(win.id)}
    >
      {/* Log content */}
      <Box
        ref={seguirOFim}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 12px',
          fontFamily: 'monospace',
          fontSize: 11,
          lineHeight: 1.5,
          color: '#c9d1d9',
        }}
      >
        {lines.length === 0 && !fetching && (
          <Text size="xs" c="dimmed" ta="center" py="xl">Nenhuma linha de log.</Text>
        )}
        {lines.map((line, i) => (
          <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{line}</div>
        ))}
      </Box>

    </JanelaFlutuante>
  )
}

// ── Logs Drawer ────────────────────────────────────────────────────────────────

interface LogsDrawerProps {
  containerName: string | null
  instanceId: string
  instanceName: string
  tenantId: number | null
  onClose: () => void
  onPin: (win: Omit<PinnedWindow, 'id'>) => void
}

function LogsDrawer({ containerName, instanceId, instanceName, tenantId, onClose, onPin }: LogsDrawerProps) {
  const [lines, setLines] = useState<string[]>([])
  const [fetching, setFetching] = useState(false)
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(paused)
  // Linha nova rola o log para o fim sem puxar quem rolou para cima
  // (consoleai#25060); pausado, a coleta para e nada chega.
  const seguirOFim = useSeguirOFim<HTMLDivElement>(lines)

  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  // Initial fetch + polling
  useEffect(() => {
    if (!containerName || !tenantId) return

    setLines([])
    setPaused(false)
    let cancelled = false

    const fetchInitial = async () => {
      setFetching(true)
      try {
        const res = await api.get(
          `/v1/tenants/${tenantId}/inventory/ec2/${instanceId}/docker/${encodeURIComponent(containerName)}/logs`,
          { params: { tail: 200 } },
        )
        if (!cancelled) {
          setLines(res.data.lines ?? [])
        }
      } catch {
        // ignore fetch errors silently
      } finally {
        if (!cancelled) setFetching(false)
      }
    }

    fetchInitial()

    const timer = setInterval(async () => {
      if (pausedRef.current || cancelled) return
      try {
        const res = await api.get(
          `/v1/tenants/${tenantId}/inventory/ec2/${instanceId}/docker/${encodeURIComponent(containerName)}/logs`,
          { params: { since: '4s' } },
        )
        if (!cancelled) {
          const newLines: string[] = res.data.lines ?? []
          if (newLines.length > 0) {
            setLines(prev => [...prev, ...newLines])
          }
        }
      } catch {
        // ignore poll errors silently
      }
    }, 3000)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [containerName, instanceId, tenantId])

  const handleClear = () => setLines([])

  return (
    <ResizableDrawer
      storageKey="logs-drawer-width"
      defaultWidth={700}
      minWidth={450}
      opened={!!containerName}
      onClose={onClose}
      title={
        <Group gap={8}>
          <IconBrandDocker size={18} color="#1D72B8" />
          <Text fw={600} size="sm">Logs — {containerName?.replace(/^\//, '')}</Text>
          {fetching && <Loader size={12} color="blue" />}
        </Group>
      }
      position="right"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Group justify="space-between" mb="xs">
        <Button
          size="xs"
          variant="light"
          color="blue"
          leftSection={<IconPin size={12} />}
          onClick={() => {
            if (!containerName || !tenantId) return
            onPin({
              containerName,
              instanceId,
              instanceName,
              tenantId,
              position: { x: Math.max(40, window.innerWidth - 660), y: 80 },
              size: { w: 600, h: 420 },
            })
            onClose()
          }}
        >
          Pinar
        </Button>
        <Group gap={8}>
          <Button
            size="xs"
            variant="subtle"
            color={paused ? 'green' : 'orange'}
            onClick={() => setPaused(p => !p)}
          >
            {paused ? 'Retomar' : 'Pausar'}
          </Button>
          <Button size="xs" variant="subtle" color="gray" onClick={handleClear}>
            Limpar
          </Button>
        </Group>
      </Group>

      <Box
        ref={seguirOFim}
        style={{
          backgroundColor: 'var(--ai-console-bg)',
          borderRadius: 6,
          padding: '12px',
          minHeight: 400,
          maxHeight: 'calc(100vh - 160px)',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: 12,
          lineHeight: 1.5,
          color: '#c9d1d9',
        }}
      >
        {lines.length === 0 && !fetching && (
          <Text size="xs" c="dimmed" ta="center" py="xl">Nenhuma linha de log.</Text>
        )}
        {lines.map((line, i) => (
          <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{line}</div>
        ))}
      </Box>
    </ResizableDrawer>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const POLL_BASE = 30_000
const POLL_FAST = 10_000

export function Recursos() {
  const tenantId = useCurrentTenant()?.id
  // Spec 2026-10-08 D18: `/recursos?aba=secrets&secret=<arn>`, o link que a
  // entrega do ajuste livre comenta no chamado.
  const [busca] = useSearchParams()
  const [activeTab, setActiveTab] = useState<string | null>(() => abaInicial(busca))
  const linkDoSecretAplicado = useRef(false)
  const [restartTarget, setRestartTarget] = useState<EC2Instance | null>(null)
  const [forceRestartTarget, setForceRestartTarget] = useState<EC2Instance | null>(null)
  const [dockerTarget, setDockerTarget] = useState<EC2Instance | null>(null)
  const [deployTarget, setDeployTarget] = useState<DeployTarget | null>(null)
  const [metricsTarget, setMetricsTarget] = useState<MetricsTarget | null>(null)
  const [networkTarget, setNetworkTarget] = useState<NetworkTarget | null>(null)
  const [healthTarget, setHealthTarget] = useState<HealthTarget | null>(null)
  const [restartingIds, setRestartingIds] = useState<Set<string>>(new Set())
  const [forceRestartingIds, setForceRestartingIds] = useState<Set<string>>(new Set())
  const [selectedSecret, setSelectedSecret] = useState<SecretItem | null>(null)
  const [pinnedWindows, setPinnedWindows] = useState<PinnedWindow[]>([])
  const [fileBrowserTarget, setFileBrowserTarget] = useState<FileBrowserTarget | null>(null)
  const [pinnedFileBrowsers, setPinnedFileBrowsers] = useState<PinnedFileBrowser[]>([])

  const pinWindow = useCallback((win: Omit<PinnedWindow, 'id'>) => {
    const id = `${win.instanceId}__${win.containerName}__${Date.now()}`
    // offset each new window slightly so they don't stack exactly
    setPinnedWindows(prev => {
      const offset = prev.length * 24
      return [...prev, { ...win, id, position: { x: win.position.x - offset, y: win.position.y + offset } }]
    })
  }, [])

  const closePinnedWindow = useCallback((id: string) => {
    setPinnedWindows(prev => prev.filter(w => w.id !== id))
  }, [])

  const pinFileBrowser = useCallback((win: Omit<PinnedFileBrowser, 'id'>) => {
    const id = `fb__${win.instanceId}__${Date.now()}`
    setPinnedFileBrowsers(prev => {
      const offset = prev.length * 40
      return [...prev, { ...win, id, position: { x: 100 + offset, y: 100 + offset } }]
    })
    setFileBrowserTarget(null)
  }, [])

  const closePinnedFileBrowser = useCallback((id: string) => {
    setPinnedFileBrowsers(prev => prev.filter(w => w.id !== id))
  }, [])

  const addOptimisticId = useCallback((
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    instanceId: string,
    ttl: number,
  ) => {
    setter(prev => new Set([...prev, instanceId]))
    setTimeout(() => {
      setter(prev => {
        const next = new Set(prev)
        next.delete(instanceId)
        return next
      })
    }, ttl)
  }, [])

  const {
    data: ec2Data, isLoading: ec2Loading, error: ec2Error,
  } = useQuery<Ec2ApiResponse, Error>({
    queryKey: ['inventory-ec2', tenantId],
    queryFn: async () => {
      const res = await api.get<Ec2ApiResponse>(`/v1/tenants/${tenantId}/inventory/ec2`)
      return res.data
    },
    enabled: !!tenantId,
    retry: false,
    refetchInterval: (query) => {
      const instances = (query.state.data as Ec2ApiResponse | undefined)?.instances ?? []
      const hasTransitional = instances.some(i => TRANSITIONAL_STATES.has(i.state))
      return hasTransitional ? POLL_FAST : POLL_BASE
    },
    refetchIntervalInBackground: false,
  })

  const {
    data: elbData, isLoading: elbLoading, error: elbError,
  } = useQuery<ElbApiResponse, Error>({
    queryKey: ['inventory-elb', tenantId],
    queryFn: async () => {
      const res = await api.get<ElbApiResponse>(`/v1/tenants/${tenantId}/inventory/elb`)
      return res.data
    },
    enabled: !!tenantId,
    retry: false,
    refetchInterval: POLL_BASE,
    refetchIntervalInBackground: false,
  })

  const {
    data: domainsData, isLoading: domainsLoading, error: domainsError,
  } = useQuery<DomainsApiResponse, Error>({
    queryKey: ['inventory-domains', tenantId],
    queryFn: async () => {
      const res = await api.get<DomainsApiResponse>(`/v1/tenants/${tenantId}/inventory/domains`)
      return res.data
    },
    enabled: !!tenantId,
    retry: false,
    refetchInterval: POLL_BASE,
    refetchIntervalInBackground: false,
  })

  const {
    data: s3Data, isLoading: s3Loading, error: s3Error,
  } = useQuery<S3ApiResponse, Error>({
    queryKey: ['inventory-s3', tenantId],
    queryFn: async () => {
      const res = await api.get<S3ApiResponse>(`/v1/tenants/${tenantId}/inventory/s3`)
      return res.data
    },
    enabled: !!tenantId,
    retry: false,
    refetchInterval: POLL_BASE,
    refetchIntervalInBackground: false,
  })

  const {
    data: secretsData, isLoading: secretsLoading, error: secretsError,
  } = useQuery<SecretsApiResponse, Error>({
    queryKey: ['inventory-secrets', tenantId],
    queryFn: async () => {
      const res = await api.get<SecretsApiResponse>(`/v1/tenants/${tenantId}/inventory/secrets`)
      return res.data
    },
    enabled: !!tenantId,
    retry: false,
    refetchInterval: POLL_BASE,
    refetchIntervalInBackground: false,
  })

  const {
    data: vpnData, isLoading: vpnLoading,
  } = useQuery<VpnApiResponse, Error>({
    queryKey: ['inventory-vpn', tenantId],
    queryFn: async () => {
      const res = await api.get<VpnApiResponse>(`/v1/tenants/${tenantId}/inventory/vpn`)
      return res.data
    },
    enabled: !!tenantId,
    retry: false,
    refetchInterval: POLL_BASE,
    refetchIntervalInBackground: false,
  })
  const vpnItemsCount = (vpnData?.site_to_site?.length ?? 0) + (vpnData?.client_vpn?.length ?? 0)
  const vpnVisible = vpnItemsCount > 0 // auto-descoberta: esconde quando vazio/denied/disabled

  // Se a aba VPN some por refetch (tenant sem VPN), evita painel em branco retornando para EC2
  useEffect(() => {
    if (activeTab === 'vpn' && !vpnVisible && !vpnLoading) {
      setActiveTab('ec2')
    }
  }, [activeTab, vpnVisible, vpnLoading])

  const ec2Items: EC2Instance[] = ec2Error ? [] : (ec2Data?.instances ?? [])
  const elbItems: ElbItem[] = elbError ? [] : (elbData?.load_balancers ?? [])
  const domainItems: DomainItem[] = domainsError ? [] : (domainsData?.domains ?? [])
  const s3Items: S3BucketItem[] = s3Error ? [] : (s3Data?.buckets ?? [])
  const secretItems: SecretItem[] = secretsError ? [] : (secretsData?.secrets ?? [])

  // O drawer do secret do link abre uma vez, quando ele chega na lista.
  useEffect(() => {
    if (linkDoSecretAplicado.current) return
    const alvo = secretDoLink(busca, secretItems)
    if (alvo) {
      linkDoSecretAplicado.current = true
      setSelectedSecret(alvo)
    }
  }, [busca, secretItems])

  const hasUnexpectedError =
    (ec2Error && ![404, 500].includes((ec2Error as { response?: { status?: number } }).response?.status ?? 0)) ||
    (elbError && ![404, 500].includes((elbError as { response?: { status?: number } }).response?.status ?? 0)) ||
    (domainsError && ![404, 500].includes((domainsError as { response?: { status?: number } }).response?.status ?? 0))

  return (
    <Stack gap="xl">
      {hasUnexpectedError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Ocorreu um erro ao carregar alguns recursos. Tente novamente mais tarde.
        </Alert>
      )}

      <WidgetCard accent="primary" p={0}>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List style={{ backgroundColor: 'var(--ai-body-bg)', borderBottom: '1px solid var(--ai-border-light)', padding: '0 16px' }}>
          <Tabs.Tab value="ec2" data-testid="tab-ec2">
            EC2 Instances{!ec2Loading && ` (${ec2Items.length})`}
          </Tabs.Tab>
          <Tabs.Tab value="elb">
            Load Balancers{!elbLoading && ` (${elbItems.length})`}
          </Tabs.Tab>
          <Tabs.Tab value="domains">
            Domínios{!domainsLoading && ` (${domainItems.length})`}
          </Tabs.Tab>
          <Tabs.Tab value="s3">
            S3{!s3Loading && ` (${s3Items.length})`}
          </Tabs.Tab>
          <Tabs.Tab value="secrets">
            Secrets Manager{!secretsLoading && ` (${secretItems.length})`}
          </Tabs.Tab>
          {vpnVisible && (
            <Tabs.Tab value="vpn" data-testid="tab-vpn">
              VPN{!vpnLoading && ` (${vpnItemsCount})`}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="ec2" pt="md">
          {ec2Loading ? (
            <TableSkeleton />
          ) : (
            <EC2Table
              items={ec2Items}
              onRestart={setRestartTarget}
              onForceRestart={setForceRestartTarget}
              onDocker={setDockerTarget}
              onDeploy={(inst) => setDeployTarget({ instanceId: inst.instance_id, instanceName: inst.name ?? inst.instance_id, tenantId: tenantId! })}
              onMetrics={(inst) => setMetricsTarget({ instanceId: inst.instance_id, instanceName: inst.name ?? inst.instance_id, tenantId: tenantId! })}
              onNetwork={(inst) => setNetworkTarget({ instanceId: inst.instance_id, instanceName: inst.name ?? inst.instance_id, tenantId: tenantId! })}
              onHealth={(inst) => setHealthTarget({ instanceId: inst.instance_id, instanceName: inst.name ?? inst.instance_id, tenantId: tenantId! })}
              onFiles={(inst) => setFileBrowserTarget({
                instanceId: inst.instance_id,
                instanceName: inst.name ?? inst.instance_id,
              })}
              restartingIds={restartingIds}
              forceRestartingIds={forceRestartingIds}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="elb" pt="md">
          {elbLoading ? <TableSkeleton /> : <ElbTable items={elbItems} />}
        </Tabs.Panel>

        <Tabs.Panel value="domains" pt="md">
          {domainsLoading ? <TableSkeleton /> : <DomainTable items={domainItems} tenantId={tenantId!} />}
        </Tabs.Panel>

        <Tabs.Panel value="s3" pt="md">
          {s3Loading ? <TableSkeleton /> : <S3Table items={s3Items} tenantId={tenantId!} />}
        </Tabs.Panel>

        <Tabs.Panel value="secrets" pt="md">
          {secretsLoading ? <TableSkeleton /> : <SecretsTable items={secretItems} onSelect={setSelectedSecret} />}
          {selectedSecret && tenantId && (
            <SecretsDrawer secret={selectedSecret} tenantId={tenantId} onClose={() => setSelectedSecret(null)} />
          )}
        </Tabs.Panel>

        {vpnVisible && (
          <Tabs.Panel value="vpn" pt="md">
            {vpnLoading || !vpnData ? <TableSkeleton /> : <VpnPanel data={vpnData} tenantId={tenantId!} />}
          </Tabs.Panel>
        )}
      </Tabs>
      </WidgetCard>

      <ActionModal
        mode="restart"
        instance={restartTarget}
        tenantId={tenantId ?? 0}
        onClose={() => setRestartTarget(null)}
        onSuccess={(id) => addOptimisticId(setRestartingIds, id, 120_000)}
      />

      <ActionModal
        mode="force-restart"
        instance={forceRestartTarget}
        tenantId={tenantId ?? 0}
        onClose={() => setForceRestartTarget(null)}
        onSuccess={(id) => addOptimisticId(setForceRestartingIds, id, 600_000)}
      />

      <DockerDrawer
        instance={dockerTarget}
        tenantId={tenantId}
        onClose={() => setDockerTarget(null)}
        onPin={pinWindow}
      />

      <DeployModal target={deployTarget} onClose={() => setDeployTarget(null)} />

      <MetricsDrawer target={metricsTarget} onClose={() => setMetricsTarget(null)} />

      <NetworkDrawer target={networkTarget} onClose={() => setNetworkTarget(null)} />

      <HealthDrawer target={healthTarget} onClose={() => setHealthTarget(null)} />

      <FileBrowserDrawer
        target={fileBrowserTarget}
        tenantId={tenantId ?? null}
        onClose={() => setFileBrowserTarget(null)}
        onPin={pinFileBrowser}
      />

      {pinnedFileBrowsers.map(w => (
        <FloatingFileBrowser key={w.id} win={w} onClose={closePinnedFileBrowser} />
      ))}

      {pinnedWindows.map(w => (
        <FloatingLogWindow key={w.id} win={w} onClose={closePinnedWindow} />
      ))}
    </Stack>
  )
}
